import i18n from "i18next";

const BASE = "";

/**
 * Map HTTP status codes to user-friendly error descriptions.
 * The raw status code is hidden — users see actionable text instead.
 */
// Exported so pages that hand-roll a fetch (SettingsPage's integration cards)
// can honour the same "users never see a raw status code" convention instead
// of inventing their own error text.
export function friendlyStatus(status: number): string {
	if (status === 401 || status === 403) return "Permission denied";
	if (status === 404) return "Not found";
	if (status === 409) return "Conflict — resource already exists or was modified";
	if (status === 413) return "File too large";
	if (status === 400 || status === 422) return i18n.t("errors.invalidInput", "Check the entered values and try again") || "Check the entered values and try again";
	if (status === 429) return "Rate limited — try again shortly";
	if (status >= 500) return "Server error — check that the server is running";
	return `Unexpected error (${status})`;
}

/** Throw a user-friendly error for failed fetch responses. */
export async function responseError(res: Response): Promise<string> {
  let message = friendlyStatus(res.status);
  // Validation/conflict explanations are intended for users. Never expose a
  // server traceback or an HTML proxy error as a form validation message.
  if (res.status >= 400 && res.status < 500) {
    try {
      const body = await res.clone().json();
      const detail = body?.detail ?? body?.message;
      if (typeof detail === "string") message = detail;
      else if (Array.isArray(detail) && detail.length) message = detail.map((item) => {
        const field = Array.isArray(item.loc) ? item.loc.filter((x: unknown) => x !== "body").join(".") : "";
        return `${field ? `${field}: ` : ""}${typeof item.msg === "string" ? item.msg : message}`;
      }).join("; ");
      else if (detail && typeof detail.message === "string") message = detail.message;
    } catch { /* Non-JSON failures use the status explanation. */ }
  }
  return message;
}

async function throwIfNotOk(res: Response, action: string): Promise<void> {
  if (!res.ok) throw new Error(`${action}: ${await responseError(res)}`);
}

export interface Conversation {
	id: string;
	title: string;
	created_at: string;
	updated_at: string;
	archived?: boolean;
	pinned?: boolean;
	/** Bot this thread is bound to (A1, 2026-09-02); '' / absent = the main agent. */
	agent_key?: string;
}

export interface ChatMessage {
	id?: string;
	role: "user" | "assistant" | "tool";
	content: string;
	streaming?: boolean;
	created_at?: string;
	/**
	 * 2026-05-23 — LLM model id that produced this assistant message
	 * (e.g. ``"anthropic/claude-sonnet-4-5"``, ``"ollama/qwen2.5:14b"``).
	 * Empty string / absent on user + tool rows AND on legacy
	 * pre-migration assistant rows.
	 */
	model?: string;
	/** The bot that authored this reply (provenance.agent_key), absent for the main agent. */
	agent_key?: string;
	/** A3: set when a ROUTINE posted this reply — {name, status}. */
	routine?: { name: string; status: string };
	/** Image attachments on a user message (2026-07-07). */
	attachments?: ApiAttachment[];
	/**
	 * Message branching (2026-07-07). ``branch_count`` is how many sibling
	 * assistant replies exist for this turn (1 = unbranched); ``branch_index``
	 * is this active reply's 0-based position among them. The chat UI renders a
	 * ``< n/m >`` switcher only when branch_count > 1.
	 */
	branch_index?: number;
	branch_count?: number;
}

export interface ApiAttachment {
	id: string;
	mime_type: string;
	data_url: string;
}

export interface ChatResponse {
	conversation_id: string;
	message: string;
}

/**
 * The LLM provider's last progress event for the turn's current wait
 * (server: llm/progress.py → TurnRegistry.note). `retry` after a timed-out
 * attempt, `fallback` when the primary model is abandoned for another.
 */
export interface TurnNote {
	kind: "retry" | "fallback" | string;
	method?: string;
	model?: string;
	attempt?: number;
	max_attempts?: number;
	reason?: string;
	waited_s?: number;
	delay_s?: number;
	fallback?: string;
}

export interface ConversationStatus {
	conversation_id: string;
	// idle = no turn running; anything else = in-flight
	phase: "idle" | "thinking" | "tool" | "streaming" | "done" | "error" | "cancelled";
	started_at?: string;
	message_id?: string | null;
	last_tool_name?: string | null;
	tools_attempted?: string[];
	tools_succeeded?: number;
	tools_failed?: number;
	last_chunk_at?: string | null;
	round_index?: number;
	error?: string | null;
	note?: TurnNote | null;
	note_at?: string | null;
}

export async function fetchConversations(includeArchived?: boolean): Promise<Conversation[]> {
	const params = includeArchived ? "?include_archived=true" : "";
	const res = await fetch(`${BASE}/api/conversations${params}`);
	await throwIfNotOk(res, "Failed to fetch conversations");
	return res.json();
}

export interface MessagesPage {
	items: ChatMessage[];
	/** True when older messages exist beyond this page (scroll-to-top loads them). */
	has_more: boolean;
}

export async function fetchMessages(
	conversationId: string,
	before?: string,
	limit = 100,
): Promise<MessagesPage> {
	const params = new URLSearchParams({ limit: String(limit) });
	if (before) params.set("before", before);
	const res = await fetch(`${BASE}/api/conversations/${conversationId}/messages?${params}`);
	await throwIfNotOk(res, "Failed to fetch messages");
	const body = await res.json();
	// Backend returns {items, has_more}; tolerate a legacy bare list during a
	// partial deploy so the pane never renders empty.
	if (Array.isArray(body)) return { items: body, has_more: false };
	return { items: body.items ?? [], has_more: !!body.has_more };
}

/**
 * Message branching (2026-07-07). Switch the active reply of a branch group to
 * the sibling at ``index`` (0-based, oldest-first). ``messageId`` is the
 * currently-active assistant reply. Returns the new active reply's id; the
 * caller reloads the conversation to pick up the switched path.
 */
export async function switchBranch(
	conversationId: string,
	messageId: string,
	index: number,
): Promise<{ ok: boolean; active_message_id: string }> {
	const res = await fetch(
		`${BASE}/api/conversations/${conversationId}/messages/${messageId}/branch`,
		{
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ index }),
		},
	);
	await throwIfNotOk(res, "Failed to switch branch");
	return res.json();
}

export async function fetchConversationStatus(
	conversationId: string,
): Promise<ConversationStatus> {
	const res = await fetch(`${BASE}/api/conversations/${conversationId}/status`);
	await throwIfNotOk(res, "Failed to fetch conversation status");
	return res.json();
}

// Thumbs up/down on an assistant reply. `rating: null` retracts a prior vote.
// The server records it into activity + the Bayesian trust engine.
export async function sendMessageFeedback(
	conversationId: string,
	messageId: string,
	rating: "up" | "down" | null,
): Promise<void> {
	const res = await fetch(
		`${BASE}/api/conversations/${conversationId}/messages/${messageId}/feedback`,
		{
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ rating }),
		},
	);
	await throwIfNotOk(res, "Failed to send feedback");
}

// Rename a conversation (overrides the LLM auto-title with the user's label).
export async function renameConversation(
	conversationId: string,
	title: string,
): Promise<void> {
	const res = await fetch(`${BASE}/api/conversations/${conversationId}/title`, {
		method: "PATCH",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ title }),
	});
	await throwIfNotOk(res, "Failed to rename conversation");
}

// Pin / unpin a conversation (pinned threads sort to the top of the sidebar).
export async function setConversationPinned(
	conversationId: string,
	pinned: boolean,
): Promise<void> {
	const res = await fetch(`${BASE}/api/conversations/${conversationId}/pin`, {
		method: "PATCH",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ pinned }),
	});
	await throwIfNotOk(res, "Failed to pin conversation");
}

// ── Bot threads (A1, 2026-09-02) ──────────────────────────────────────

export interface ConversationAgent {
	conversation_id: string;
	agent_key: string;
	name: string;
	role: string;
}

export async function fetchConversationAgent(conversationId: string): Promise<ConversationAgent> {
	const res = await fetch(`${BASE}/api/conversations/${conversationId}/agent`);
	await throwIfNotOk(res, "Failed to read the thread's agent");
	return res.json();
}

/** Bind a thread to an agent; agentKey "" hands it back to the main agent. */
export async function setConversationAgent(
	conversationId: string,
	agentKey: string,
): Promise<{ ok: boolean; agent_key: string; name: string }> {
	const res = await fetch(`${BASE}/api/conversations/${conversationId}/agent`, {
		method: "PATCH",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ agent_key: agentKey }),
	});
	await throwIfNotOk(res, "Failed to bind the thread to an agent");
	return res.json();
}

// ── Group threads (bots B5, 2026-09-06) ────────────────────────────────

export interface ThreadParticipant {
	key: string;
	name: string;
	role: string;
	active: boolean;
}

export interface ThreadParticipants {
	conversation_id: string;
	participants: ThreadParticipant[];
	/** The bound agent — the thread's default responder ('' = main). */
	agent_key: string;
	total: number;
}

export async function fetchParticipants(conversationId: string): Promise<ThreadParticipants> {
	const res = await fetch(`${BASE}/api/conversations/${conversationId}/participants`);
	await throwIfNotOk(res, "Failed to read the thread's participants");
	return res.json();
}

/** Bring an agent into the thread (the button form of @Name). */
export async function addParticipant(conversationId: string, agentKey: string): Promise<ThreadParticipants & { added: boolean }> {
	const res = await fetch(`${BASE}/api/conversations/${conversationId}/participants/${encodeURIComponent(agentKey)}`, { method: "POST" });
	await throwIfNotOk(res, "Failed to add the agent to the thread");
	return res.json();
}

export async function removeParticipant(conversationId: string, agentKey: string): Promise<ThreadParticipants & { removed: boolean }> {
	const res = await fetch(`${BASE}/api/conversations/${conversationId}/participants/${encodeURIComponent(agentKey)}`, { method: "DELETE" });
	await throwIfNotOk(res, "Failed to remove the agent from the thread");
	return res.json();
}

/** Hand a task to another agent: assigned_agent moves, the task's log says why. */
export async function handOverTask(
	slug: string,
	agentKey: string,
	reason = "",
	project?: string,
): Promise<{ slug: string; assigned_agent: string; previous_agent: string; name: string; path: string }> {
	const q = project ? `?project=${encodeURIComponent(project)}` : "";
	const res = await fetch(`${BASE}/api/tasks/${encodeURIComponent(slug)}/agent${q}`, {
		method: "PUT",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ agent_key: agentKey, reason }),
	});
	await throwIfNotOk(res, "Failed to hand the task over");
	return res.json();
}

export interface RosterAgent {
	key: string;
	name: string;
	role: string;
	status: string;
	type?: string;
	origin?: string;
	roster_group?: string;
}

/** The agent roster (GET /api/orchestrator/agents) — see lib/roster.ts for the cached hook. */
export async function fetchAgentRoster(): Promise<RosterAgent[]> {
	const res = await fetch(`${BASE}/api/orchestrator/agents`);
	await throwIfNotOk(res, "Failed to fetch the agent roster");
	const body = await res.json();
	return Array.isArray(body) ? body : [];
}

export async function sendChat(
	message: string,
	conversationId?: string,
): Promise<ChatResponse> {
	const res = await fetch(`${BASE}/api/chat`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ message, conversation_id: conversationId }),
	});
	await throwIfNotOk(res, "Chat request failed");
	return res.json();
}

// --- Vault ---

export interface VaultFile {
	path: string;
	content: string;
	metadata: Record<string, unknown>;
}

export interface VaultStatus {
	identity_files: string[];
	agent_count: number;
	journal_count: number;
	task_count: number;
	goal_count: number;
	onboarded: boolean;
}

export interface AgentSummary {
	name: string;
	role: string;
	status: string;
}

export async function fetchVaultStatus(): Promise<VaultStatus> {
	const res = await fetch(`${BASE}/api/vault/status`);
	await throwIfNotOk(res, "Failed to fetch vault status");
	return res.json();
}

export async function fetchIdentityFiles(): Promise<Record<string, VaultFile>> {
	const res = await fetch(`${BASE}/api/vault/identity`);
	await throwIfNotOk(res, "Failed to fetch identity");
	return res.json();
}

export async function fetchVaultFile(path: string): Promise<VaultFile> {
	const res = await fetch(`${BASE}/api/vault/file/${path}`);
	await throwIfNotOk(res, `Failed to fetch ${path}`);
	return res.json();
}

export async function updateVaultFile(
	path: string,
	content: string,
	metadata?: Record<string, unknown>,
): Promise<VaultFile> {
	const res = await fetch(`${BASE}/api/vault/file/${path}`, {
		method: "PUT",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ content, metadata }),
	});
	await throwIfNotOk(res, `Failed to update ${path}`);
	return res.json();
}

export async function fetchAgents(): Promise<AgentSummary[]> {
	const res = await fetch(`${BASE}/api/vault/agents`);
	await throwIfNotOk(res, "Failed to fetch agents");
	return res.json();
}

export async function fetchTrust(): Promise<VaultFile> {
	const res = await fetch(`${BASE}/api/vault/trust`);
	await throwIfNotOk(res, "Failed to fetch trust");
	return res.json();
}

// --- Onboarding ---

export interface OnboardingResponse {
	message: string;
	conversation_id: string;
	complete: boolean;
	profile: string | null;
	contract: string | null;
}

export async function fetchOnboardingStatus(): Promise<{ onboarded: boolean }> {
	const res = await fetch(`${BASE}/api/onboarding/status`);
	await throwIfNotOk(res, "Failed to check onboarding");
	return res.json();
}

export async function sendOnboardingChat(
	message: string,
	conversationId?: string,
): Promise<OnboardingResponse> {
	const res = await fetch(`${BASE}/api/onboarding/chat`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ message, conversation_id: conversationId }),
	});
	await throwIfNotOk(res, "Onboarding chat failed");
	return res.json();
}

/** Deterministic form-based onboarding — no LLM, instant. */
export interface OnboardingFormData {
	name: string;
	role?: string;
	company?: string;
	communication_style?: "concise" | "detailed" | "balanced";
	timezone?: string;
	work_hours?: string;
	proactivity?: "observer" | "advisor" | "assistant" | "partner";
	privacy_mode?: "cloud" | "local" | "both";
}

export async function submitOnboardingForm(data: OnboardingFormData): Promise<{ ok: boolean }> {
	const res = await fetch(`${BASE}/api/onboarding/form`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(data),
	});
	await throwIfNotOk(res, "Onboarding form failed");
	return res.json();
}

/** Skip the conversational onboarding and install a baseline PROFILE +
 *  CONTRACT. The fast path for users who don't want a guided tour. */
export async function quickStartOnboarding(): Promise<{ ok: boolean }> {
	const res = await fetch(`${BASE}/api/onboarding/quick-start`, {
		method: "POST",
	});
	await throwIfNotOk(res, "Quick-start failed");
	return res.json();
}

// --- Journal ---

/** Resolved body for a per-file note referenced by a stub link in the
 * daily journal's ``## Notes`` section. Surfaced as ``note_bodies``
 * alongside ``content`` so the Journal-tab renderer can expand stubs
 * inline without a second round-trip. */
export interface JournalNoteBody {
	title: string;
	body: string;
	timestamp: string;
}

export interface JournalEntry {
	date: string;
	content: string;
	metadata: Record<string, unknown>;
	/** Slug → resolved body map. Present on daily user-journal responses
	 * when any rich-per-file notes exist; absent / empty otherwise. */
	note_bodies?: Record<string, JournalNoteBody> | null;
}

export interface JournalListItem {
	date: string;
	path: string;
}

export interface JournalDateItem {
	date: string;
	types: string[];
	flags: string[];
}

export async function fetchJournalToday(): Promise<JournalEntry> {
	const res = await fetch(`${BASE}/api/journal/today`);
	await throwIfNotOk(res, "Failed to fetch journal");
	return res.json();
}

export async function fetchJournalEntries(limit = 30): Promise<JournalListItem[]> {
	const res = await fetch(`${BASE}/api/journal/entries?limit=${limit}`);
	await throwIfNotOk(res, "Failed to fetch journal entries");
	const body = await res.json();
	// Backend returns {items, total, has_more} per Rule 20; tolerate the
	// legacy bare-list shape during partial deploys.
	return Array.isArray(body) ? body : (body.items ?? []);
}

export async function fetchJournalEntry(date: string): Promise<JournalEntry> {
	const res = await fetch(`${BASE}/api/journal/entries/${date}`);
	await throwIfNotOk(res, "Failed to fetch journal entry");
	return res.json();
}

export async function fetchJournalDates(limit = 30): Promise<JournalDateItem[]> {
	const res = await fetch(`${BASE}/api/journal/dates?limit=${limit}`);
	await throwIfNotOk(res, "Failed to fetch journal dates");
	const body = await res.json();
	return Array.isArray(body) ? body : (body.items ?? []);
}

export async function fetchJournalFile(date: string, fileType: string): Promise<JournalEntry> {
	const res = await fetch(`${BASE}/api/journal/file/${date}/${fileType}`);
	await throwIfNotOk(res, `No ${fileType} for ${date}`);
	return res.json();
}

export async function appendJournal(
	text: string,
	section = "Thoughts",
	author = "HUMAN",
): Promise<void> {
	const res = await fetch(`${BASE}/api/journal/append`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ text, section, author }),
	});
	await throwIfNotOk(res, "Failed to append journal");
}

export interface JournalNoteResult {
	/** "line" = inline quick note in the daily journal's ## Notes section.
	 *  "rich" = per-file note at journal/user/notes/<date>/<slug>.md with
	 *  a stub link in the daily journal. Routing decided server-side. */
	kind?: "line" | "rich";
	status?: string;
	section?: string;
	author?: string;
	timestamp: string;
	title: string;
	body_chars: number;
	deduped: boolean;
	slug?: string;
	path?: string;
}

export async function writeJournalNote(
	body: string,
	title?: string,
): Promise<JournalNoteResult> {
	const payload: { body: string; title?: string } = { body };
	if (title?.trim()) payload.title = title.trim();
	const res = await fetch(`${BASE}/api/journal/note`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(payload),
	});
	await throwIfNotOk(res, "Failed to write note");
	return res.json();
}

export interface JournalNote {
	kind: "line" | "rich";
	timestamp: string;
	title: string;
	body: string;
	raw: string;
	slug: string;  // non-empty for stub-linked rich notes (per-note files)
}

export interface JournalSingleNote {
	date: string;
	slug: string;
	timestamp: string;
	title: string;
	body: string;
	path: string;
}

export async function fetchJournalNote(date: string, slug: string): Promise<JournalSingleNote> {
	const res = await fetch(`${BASE}/api/journal/note/${date}/${slug}`);
	await throwIfNotOk(res, "Failed to load note");
	return res.json();
}

export interface JournalInventoryNote {
	slug: string;
	title: string;
	timestamp: string;
	has_images: boolean;
	body_chars: number;
}

export interface JournalInventory {
	date: string;
	file_types: { type: string }[];
	notes: JournalInventoryNote[];
}

export async function fetchJournalInventory(date: string): Promise<JournalInventory> {
	const res = await fetch(`${BASE}/api/journal/inventory/${date}`);
	await throwIfNotOk(res, "Failed to load date inventory");
	return res.json();
}

export async function updateJournalNote(
	date: string,
	slug: string,
	{ title, body }: { title?: string | null; body?: string | null },
): Promise<JournalSingleNote> {
	const payload: Record<string, string | null> = {};
	if (title !== undefined) payload.title = title;
	if (body !== undefined) payload.body = body;
	const res = await fetch(`${BASE}/api/journal/note/${date}/${slug}`, {
		method: "PUT",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(payload),
	});
	await throwIfNotOk(res, "Failed to save note");
	return res.json();
}

export interface JournalLineEditResult {
	ok: boolean;
	date: string;
	section: string;
	raw: string;   // the rewritten line (timestamp prefix preserved)
	text: string;  // the new body text
	old_raw: string;
}

/** Edit a one-liner journal entry in place (any section). The timestamp
 *  prefix is preserved server-side; only the body text changes. */
export async function updateJournalLine(
	date: string,
	section: string,
	{ old_raw, new_text, occurrence = 0 }: { old_raw: string; new_text: string; occurrence?: number },
): Promise<JournalLineEditResult> {
	const res = await fetch(`${BASE}/api/journal/line/${date}/${encodeURIComponent(section)}`, {
		method: "PUT",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ old_raw, new_text, occurrence }),
	});
	await throwIfNotOk(res, "Failed to save entry");
	return res.json();
}

/** Delete a one-liner journal entry. Idempotent — a missing line returns
 *  ``{deleted: false}`` rather than erroring. */
export async function deleteJournalLine(
	date: string,
	section: string,
	{ old_raw, occurrence = 0 }: { old_raw: string; occurrence?: number },
): Promise<{ deleted: boolean; section: string; reason?: string }> {
	const res = await fetch(`${BASE}/api/journal/line/${date}/${encodeURIComponent(section)}`, {
		method: "DELETE",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ old_raw, occurrence }),
	});
	await throwIfNotOk(res, "Failed to delete entry");
	return res.json();
}

export interface JournalNotesFeed {
	date: string;
	items: JournalNote[];
	total: number;
	has_more: boolean;
}

export async function fetchJournalNotes(date?: string): Promise<JournalNotesFeed> {
	const url = date ? `${BASE}/api/journal/notes/${date}` : `${BASE}/api/journal/notes`;
	const res = await fetch(url);
	await throwIfNotOk(res, "Failed to load notes feed");
	return res.json();
}

export interface JournalAttachment {
	path: string; // markdown-ready relative ref, e.g. ".attachments/2026-05-04/abc12345.png"
	url: string;  // API URL the UI uses to render
	size: number;
}

export async function attachJournalImage(file: File): Promise<JournalAttachment> {
	const form = new FormData();
	form.append("file", file);
	const res = await fetch(`${BASE}/api/journal/attach`, { method: "POST", body: form });
	await throwIfNotOk(res, "Failed to upload attachment");
	return res.json();
}

// --- Tasks ---

/** A slug is unique within a project, not across the vault. */
function taskProjectQuery(project?: string): string {
	return project ? `?project=${encodeURIComponent(project)}` : "";
}

export interface TaskSummary {
	kanban_position?: number;
	assigned_agent?: string;
	slug: string;
	title: string;
	status: string;
	mode: string;
	domain: string;
	priority: string;
	project: string;
	delegated: string;
	due: string;
	updated_at?: string;
}

export interface TaskDetail {
	slug: string;
	content: string;
	metadata: Record<string, unknown>;
	/** The `## Brief` text, parsed by the server's section helper (2026-09-25). */
	brief?: string;
}

export async function fetchTasks(status?: string, project?: string): Promise<TaskSummary[]> {
	const params = new URLSearchParams();
	if (status) params.set("status", status);
	if (project) params.set("project", project);
	const qs = params.toString();
	const url = qs ? `${BASE}/api/tasks?${qs}` : `${BASE}/api/tasks`;
	const res = await fetch(url);
	await throwIfNotOk(res, "Failed to fetch tasks");
	return res.json();
}

export async function createTask(
	title: string,
	brief: string,
	mode = "review",
	domain = "general",
	project?: string,
	due = "",
	priority = "normal",
	assignedAgent?: string,
): Promise<{ path: string; slug?: string; project?: string; status: string }> {
	const res = await fetch(`${BASE}/api/tasks`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ title, brief, mode, domain, project, due, priority, assigned_agent: assignedAgent }),
	});
	await throwIfNotOk(res, "Failed to create task");
	return res.json();
}

export async function reassignTask(slug: string, project: string, currentProject?: string): Promise<{ slug: string; project: string; path: string }> {
	const res = await fetch(`${BASE}/api/tasks/${encodeURIComponent(slug)}/project${taskProjectQuery(currentProject)}`, {
		method: "PUT",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ project }),
	});
	await throwIfNotOk(res, "Failed to reassign task");
	return res.json();
}

export async function renameTask(
	slug: string,
	title: string,
	project?: string,
): Promise<{ slug: string; project: string }> {
	const res = await fetch(`${BASE}/api/tasks/${encodeURIComponent(slug)}/rename${taskProjectQuery(project)}`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ title }),
	});
	await throwIfNotOk(res, "Failed to rename task");
	return res.json();
}

export async function updateTask(
	slug: string,
	body: {
		description?: string;
		due?: string;
		priority?: string;
		mode?: string;
		domain?: string;
	},
	project?: string,
): Promise<void> {
	const res = await fetch(`${BASE}/api/tasks/${encodeURIComponent(slug)}${taskProjectQuery(project)}`, {
		method: "PUT",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(body),
	});
	await throwIfNotOk(res, "Failed to update task");
}

// --- Focus sessions ---

export interface FocusSession {
	id: number;
	task_slug: string;
	task_title: string;
	project: string;
	planned_minutes: number;
	started_at: string;
	ended_at: string | null;
	state: "running" | "paused" | "completed" | "abandoned";
	paused_seconds: number;
	elapsed_seconds: number;
	remaining_seconds: number;
	/**
	 * Per-task ordinal — "your 4th session on Write the report". Counts
	 * completed sessions only, so it agrees with the "N session(s)" totals
	 * shown elsewhere. Null when no task is attached, or when the session was
	 * abandoned (which never consumes a number). On a RUNNING session this is
	 * provisional: the number it takes if it completes.
	 */
	task_sequence: number | null;
	note: string;
}

export interface FocusStats {
	daily_goal_minutes: number;
	today_seconds: number;
	yesterday_seconds: number;
	streak_days: number;
	sessions_today: number;
	week: { date: string; seconds: number }[];
	active: FocusSession | null;
}

export async function startFocusSession(
	plannedMinutes: number,
	taskSlug = "",
	note = "",
	project?: string,
): Promise<FocusSession> {
	const res = await fetch(`${BASE}/api/focus/start`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ planned_minutes: plannedMinutes, task_slug: taskSlug, note, project }),
	});
	await throwIfNotOk(res, "Failed to start focus session");
	return (await res.json()).session;
}

export async function fetchActiveFocusSession(): Promise<FocusSession | null> {
	const res = await fetch(`${BASE}/api/focus/active`);
	await throwIfNotOk(res, "Failed to fetch focus session");
	return (await res.json()).session;
}

export async function pauseFocusSession(id: number): Promise<FocusSession> {
	const res = await fetch(`${BASE}/api/focus/${id}/pause`, { method: "POST" });
	await throwIfNotOk(res, "Failed to pause focus session");
	return (await res.json()).session;
}

export async function resumeFocusSession(id: number): Promise<FocusSession> {
	const res = await fetch(`${BASE}/api/focus/${id}/resume`, { method: "POST" });
	await throwIfNotOk(res, "Failed to resume focus session");
	return (await res.json()).session;
}

export async function stopFocusSession(
	id: number,
	outcome: "completed" | "abandoned" = "completed",
): Promise<FocusSession> {
	const res = await fetch(`${BASE}/api/focus/${id}/stop`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ outcome }),
	});
	await throwIfNotOk(res, "Failed to stop focus session");
	return (await res.json()).session;
}

export async function fetchFocusStats(): Promise<FocusStats> {
	const res = await fetch(`${BASE}/api/focus/stats`);
	await throwIfNotOk(res, "Failed to fetch focus stats");
	return res.json();
}

export async function fetchFocusHistory(
	limit = 20,
	offset = 0,
	task?: string,
	project?: string,
): Promise<{ items: FocusSession[]; total: number; has_more: boolean }> {
	const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
	if (task) params.set("task", task);
	if (project !== undefined) params.set("project", project);
	const res = await fetch(`${BASE}/api/focus/history?${params}`);
	await throwIfNotOk(res, "Failed to fetch focus history");
	return res.json();
}

export async function fetchFocusTaskTotals(
	slug: string,
	project?: string,
): Promise<{
	task_slug: string;
	/** Completed sessions — agrees with the per-task ordinal. */
	sessions: number;
	seconds: number;
	/** Discarded attempts. The struggle signal, deliberately separate from
	 *  `sessions` so neither number carries two meanings. */
	abandoned: number;
}> {
	const res = await fetch(`${BASE}/api/focus/task/${encodeURIComponent(slug)}/totals${taskProjectQuery(project)}`);
	await throwIfNotOk(res, "Failed to fetch focus totals");
	return res.json();
}

export async function updateFocusGoal(dailyGoalMinutes: number): Promise<number> {
	const res = await fetch(`${BASE}/api/focus/settings`, {
		method: "PUT",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ daily_goal_minutes: dailyGoalMinutes }),
	});
	await throwIfNotOk(res, "Failed to update focus goal");
	return (await res.json()).daily_goal_minutes;
}

// ── Calendar (H4 view surface, 2026-07-24) ───────────────────────────

export interface CalendarEvent {
	event_id: string;
	source: string;
	summary: string;
	description?: string;
	location?: string;
	start: string;
	end?: string;
	all_day?: number;
	organizer_name?: string;
	organizer_email?: string;
	attendees?: string[];
	html_link?: string;
	status?: string;
	// v2 (2026-07-27): recurrence + categorisation. `occurrence_key` identifies
	// WHICH instance of a series a row represents — `event_id` is the master's
	// id and repeats across occurrences, so it is NOT a unique React key.
	rrule?: string;
	category?: string;
	tz?: string;
	occurrence_key?: string;
	is_occurrence?: boolean;
	master_event_id?: string;
	// An edited single occurrence is its own row: these name the series it
	// belongs to and the occurrence it replaced (2026-10-02).
	recurring_event_id?: string;
	original_start?: string;
	// UTC-instant sort keys the server computes for every row (start/end
	// localized through the event's own zone). The calendar uses them to
	// render rows whose `start` TEXT is wall-clock in some OTHER zone —
	// legacy Outlook-synced rows stored naive UTC — in the user's zone.
	sort_start?: string;
	sort_end?: string;
}

export interface CalendarDay {
	date: string;
	events: CalendarEvent[];
}

export interface CalendarSnapshot {
	available: boolean;
	days: CalendarDay[];
	total: number;
}

export async function fetchCalendarUpcoming(days = 7): Promise<CalendarSnapshot> {
	const res = await fetch(`${BASE}/api/calendar/upcoming?days=${days}`);
	await throwIfNotOk(res, "Failed to fetch calendar");
	return res.json();
}

export interface NewCalendarEvent {
	summary: string;
	start: string;
	end?: string;
	location?: string;
	description?: string;
	rrule?: string;
	category?: string;
	reminder_minutes_before?: number[];
}

export async function createCalendarEvent(
	payload: NewCalendarEvent,
): Promise<{ created: boolean; event: CalendarEvent; note: string }> {
	const res = await fetch(`${BASE}/api/calendar/events`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(payload),
	});
	await throwIfNotOk(res, "Failed to create event");
	return res.json();
}

export async function cancelCalendarEvent(
	eventId: string,
): Promise<{ cancelled: boolean; event_id: string }> {
	const res = await fetch(`${BASE}/api/calendar/events/${encodeURIComponent(eventId)}`, {
		method: "DELETE",
	});
	await throwIfNotOk(res, "Failed to cancel event");
	return res.json();
}

// ── Calendar v2 (2026-07-27) ─────────────────────────────────────────

export interface CalendarWindow {
	events: CalendarEvent[];
	total: number;
	has_more: boolean;
	truncated_events?: string[];
	window?: { start: string; end: string; days: number };
}

/** Events in an explicit date window, recurrence expanded. Backs the month
 * and week grids, which fetch by visible range rather than "next N days". */
export async function fetchCalendarEvents(
	start: string,
	days: number,
	limit = 200,
): Promise<CalendarWindow> {
	const params = new URLSearchParams({
		start,
		days: String(days),
		limit: String(limit),
	});
	const res = await fetch(`${BASE}/api/calendar/events?${params}`);
	await throwIfNotOk(res, "Failed to fetch calendar");
	return res.json();
}

/** Download the calendar as a standard .ics file.
 *
 * Uses a blob + object URL rather than pointing the browser at the endpoint,
 * so the download works identically whether or not the route is same-origin
 * and the filename comes from us instead of the URL's last segment. */
export async function downloadCalendarIcs(start = "", days = 0): Promise<void> {
	// Default to a wide window ANCHORED IN THE PAST. The route's own default is
	// today-forward, which is right for "send me the next quarter" but wrong for
	// a button labelled "Export" — a user clicking it expects their calendar,
	// not the half of it that hasn't happened yet.
	const from = start || `${new Date().getUTCFullYear() - 5}-01-01`;
	const span = days || 3650;
	const params = new URLSearchParams({ start: from, days: String(span) });
	const res = await fetch(`${BASE}/api/calendar/export.ics?${params}`);
	await throwIfNotOk(res, "Failed to export calendar");
	const blob = await res.blob();
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = "agntspace-calendar.ics";
	document.body.appendChild(a);
	a.click();
	a.remove();
	URL.revokeObjectURL(url);
}

export interface ProposalsSummary {
	total: number;
	by_kind: Record<string, number>;
	by_severity: Record<string, number>;
}

/** Pending-proposal counts for the dashboard card.
 *
 * `/api/proposals` is bounded-by-domain (it returns every item and computes
 * its groupings over all of them), so these are TRUE totals — not a page
 * length wearing a total's clothes. */
export async function fetchProposalsSummary(): Promise<ProposalsSummary> {
	const res = await fetch(`${BASE}/api/proposals`);
	await throwIfNotOk(res, "Failed to fetch proposals");
	const body = await res.json();
	return {
		total: body?.total ?? 0,
		by_kind: body?.by_kind ?? {},
		by_severity: body?.by_severity ?? {},
	};
}

export interface CalendarImportResult {
	created: number;
	updated: number;
	imported: number;
	skipped: { summary: string; reason: string }[];
	skipped_count: number;
	failed: number;
	events_in_file: number;
	truncated: boolean;
	calendar: string;
}

/** Import an .ics file into the calendar.
 *
 * Sends the raw bytes rather than multipart: the route accepts both, and a
 * plain body avoids depending on FormData boundary handling. The confirmation
 * header is required because import maps to the confirm-tier `create_event`
 * contract action — the user clicking the file picker IS the confirmation. */
export async function importCalendarIcs(
	file: File,
	calendar = "",
): Promise<CalendarImportResult> {
	const params = new URLSearchParams();
	if (calendar) params.set("calendar", calendar);
	else params.set("calendar", file.name.replace(/\.ics$/i, ""));
	const res = await fetch(`${BASE}/api/calendar/import.ics?${params}`, {
		method: "POST",
		headers: {
			"Content-Type": "text/calendar",
			"X-Contract-Confirmation": "yes",
		},
		body: await file.arrayBuffer(),
	});
	await throwIfNotOk(res, "Failed to import calendar");
	return res.json();
}

export interface OutlookCalendarStatus {
	available: boolean;
	connected: boolean;
	accounts?: { slug: string; calendar_access: boolean }[];
	needs_reconnect?: string[];
}

export async function fetchOutlookCalendarStatus(): Promise<OutlookCalendarStatus> {
	const res = await fetch(`${BASE}/api/calendar/outlook/status`);
	await throwIfNotOk(res, "Failed to fetch Outlook status");
	return res.json();
}

export async function syncOutlookCalendar(): Promise<{
	upserted: number;
	fetched: number;
	skipped_no_scope: string[];
}> {
	const res = await fetch(`${BASE}/api/calendar/outlook/sync`, { method: "POST" });
	await throwIfNotOk(res, "Outlook sync failed");
	return res.json();
}

export interface CalendarCategory {
	key: string;
	label: string;
	color: string;
	icon?: string;
}

export async function fetchCalendarCategories(): Promise<CalendarCategory[]> {
	const res = await fetch(`${BASE}/api/calendar/categories`);
	await throwIfNotOk(res, "Failed to fetch categories");
	const body = await res.json();
	return Array.isArray(body) ? body : (body.categories ?? []);
}

/** Advisory overlap check — never blocks a save; the UI shows a hint. */
export async function fetchCalendarConflicts(
	start: string,
	end: string,
	excludeEventId = "",
): Promise<CalendarEvent[]> {
	const params = new URLSearchParams({ start });
	if (end) params.set("end", end);
	if (excludeEventId) params.set("exclude", excludeEventId);
	const res = await fetch(`${BASE}/api/calendar/conflicts?${params}`);
	if (!res.ok) return [];   // advisory: a failed check must never block saving
	const body = await res.json();
	return Array.isArray(body) ? body : (body.conflicts ?? []);
}

export interface CalendarEventPatch {
	summary?: string;
	start?: string;
	end?: string;
	location?: string;
	description?: string;
	rrule?: string;
	category?: string;
}

export async function updateCalendarEvent(
	eventId: string,
	patch: CalendarEventPatch,
): Promise<{ updated: boolean; event: CalendarEvent }> {
	const res = await fetch(`${BASE}/api/calendar/events/${encodeURIComponent(eventId)}`, {
		method: "PATCH",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(patch),
	});
	await throwIfNotOk(res, "Failed to update event");
	return res.json();
}

/** Edit ONE occurrence of a recurring series (`scope: "this"`), or the whole
 *  series measured from it (`"all"`: the change to this occurrence's time
 *  shifts every occurrence by the same amount). Returns the row that now
 *  carries the edit — the occurrence's own row for "this", the series for
 *  "all". PATCHing the series with an occurrence's start used to move the
 *  whole series to that occurrence (2026-10-02). */
export async function updateCalendarOccurrence(
	eventId: string,
	occurrenceStart: string,
	scope: "this" | "all",
	patch: CalendarEventPatch,
): Promise<{ updated: boolean; scope: string; event: CalendarEvent }> {
	const res = await fetch(`${BASE}/api/calendar/events/${encodeURIComponent(eventId)}/occurrence`, {
		method: "PATCH",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ ...patch, occurrence_start: occurrenceStart, scope }),
	});
	await throwIfNotOk(res, "Failed to update the occurrence");
	return res.json();
}

/** Drop ONE occurrence of a recurring series, keeping the rest. */
export async function skipCalendarOccurrence(
	eventId: string,
	occurrenceStart: string,
): Promise<{ skipped: boolean }> {
	const res = await fetch(
		`${BASE}/api/calendar/events/${encodeURIComponent(eventId)}/skip-occurrence`,
		{
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ occurrence_start: occurrenceStart }),
		},
	);
	await throwIfNotOk(res, "Failed to skip occurrence");
	return res.json();
}

export interface CalendarReminder {
	reminder_id: string;
	event_id: string;
	offset_minutes: number | null;
	at_utc?: string;
	status: string;
	// Joined from the owning event so a consumer can name what it is asking
	// about without needing that event in its loaded window.
	event_summary?: string;
	event_start?: string;
	event_category?: string;
}

export async function fetchCalendarReminders(eventId: string): Promise<CalendarReminder[]> {
	const res = await fetch(
		`${BASE}/api/calendar/reminders?event_id=${encodeURIComponent(eventId)}`,
	);
	await throwIfNotOk(res, "Failed to fetch reminders");
	const body = await res.json();
	return Array.isArray(body) ? body : (body.items ?? []);
}

export async function addCalendarReminder(
	eventId: string,
	minutesBefore: number,
): Promise<{ added: boolean; reminder: CalendarReminder }> {
	const res = await fetch(
		`${BASE}/api/calendar/events/${encodeURIComponent(eventId)}/reminders`,
		{
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ minutes_before: minutesBefore }),
		},
	);
	await throwIfNotOk(res, "Failed to add reminder");
	return res.json();
}

export async function snoozeCalendarReminder(
	reminderId: string,
	minutes = 10,
): Promise<{ snoozed: boolean; minutes: number }> {
	const res = await fetch(
		`${BASE}/api/calendar/reminders/${encodeURIComponent(reminderId)}/snooze`,
		{
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ minutes }),
		},
	);
	await throwIfNotOk(res, "Failed to snooze reminder");
	return res.json();
}

export async function dismissCalendarReminder(
	reminderId: string,
): Promise<{ dismissed: boolean }> {
	const res = await fetch(
		`${BASE}/api/calendar/reminders/${encodeURIComponent(reminderId)}/dismiss`,
		{ method: "POST" },
	);
	await throwIfNotOk(res, "Failed to dismiss reminder");
	return res.json();
}

/** Every pending reminder across all events — backs the calendar page's
 * "Reminders" panel, so snooze/dismiss are reachable without opening the
 * event that owns them. */
export async function fetchPendingReminders(): Promise<CalendarReminder[]> {
	const res = await fetch(`${BASE}/api/calendar/reminders?status=pending`);
	await throwIfNotOk(res, "Failed to fetch reminders");
	const body = await res.json();
	return Array.isArray(body) ? body : (body.items ?? []);
}

export async function deleteCalendarReminder(reminderId: string): Promise<{ deleted: boolean }> {
	const res = await fetch(`${BASE}/api/calendar/reminders/${encodeURIComponent(reminderId)}`, {
		method: "DELETE",
	});
	await throwIfNotOk(res, "Failed to delete reminder");
	return res.json();
}

export interface PersonDate {
	summary: string;
	date: string;
	category: string;
	event_id: string;
}

export async function fetchPersonDates(days = 60): Promise<{ items: PersonDate[]; total: number }> {
	const res = await fetch(`${BASE}/api/calendar/person-dates?days=${days}`);
	await throwIfNotOk(res, "Failed to fetch birthdays");
	return res.json();
}

export async function recordPersonDate(payload: {
	person: string;
	date: string;
	kind?: string;
	note?: string;
}): Promise<{ recorded: boolean; person: string; date: string; next_occurrence: string }> {
	const res = await fetch(`${BASE}/api/calendar/person-dates`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(payload),
	});
	await throwIfNotOk(res, "Failed to record date");
	return res.json();
}

// --- Goals ---

export interface GoalSummary {
	slug: string;
	title: string;
	type: string;
	status: string;
	target_date: string;
	created: string;
	updated_at?: string;
}

export interface GoalDetail {
	slug: string;
	content: string;
	metadata: Record<string, unknown>;
}

export async function fetchGoals(status?: string): Promise<GoalSummary[]> {
	const qs = status ? `?status=${status}` : "";
	const res = await fetch(`${BASE}/api/goals${qs}`);
	await throwIfNotOk(res, "Failed to fetch goals");
	return res.json();
}

export async function fetchGoal(slug: string): Promise<GoalDetail> {
	const res = await fetch(`${BASE}/api/goals/${slug}`);
	await throwIfNotOk(res, "Failed to fetch goal");
	return res.json();
}

export async function deleteGoal(slug: string): Promise<{ slug: string; projects_cleaned: number }> {
	const res = await fetch(`${BASE}/api/goals/${slug}`, { method: "DELETE" });
	await throwIfNotOk(res, "Failed to delete goal");
	return res.json();
}

// --- Projects (thin client) ---

export interface ProjectLite {
	slug: string;
	title: string;
	status: string;
	related_goals: string[];
	assigned_agents: string[];
	linked_kbs: string[];
	/** Top-level projects have an empty parent. A sub-project names its parent slug. */
	parent?: string;
	updated_at?: string;
}

export async function fetchProjectsLite(): Promise<ProjectLite[]> {
	const res = await fetch(`${BASE}/api/projects`);
	await throwIfNotOk(res, "Failed to fetch projects");
	return res.json();
}

export async function fetchProjectTasks(slug: string): Promise<TaskSummary[]> {
	const res = await fetch(`${BASE}/api/projects/${slug}/tasks`);
	await throwIfNotOk(res, "Failed to fetch project tasks");
	return res.json();
}

// --- Goals reverse lookup ---

export interface GoalProjectLink {
	slug: string;
	title: string;
	status: string;
}

export async function fetchGoalProjects(slug: string): Promise<GoalProjectLink[]> {
	const res = await fetch(`${BASE}/api/goals/${slug}/projects`);
	await throwIfNotOk(res, "Failed to fetch goal projects");
	return res.json();
}

export async function updateGoal(
	slug: string,
	body: { description?: string; target_date?: string },
): Promise<void> {
	const res = await fetch(`${BASE}/api/goals/${slug}`, {
		method: "PUT",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(body),
	});
	await throwIfNotOk(res, "Failed to update goal");
}

export async function updateGoalStatus(slug: string, status: string): Promise<void> {
	const res = await fetch(`${BASE}/api/goals/${slug}/status`, {
		method: "PATCH",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ status }),
	});
	await throwIfNotOk(res, "Failed to update goal status");
}

export async function attachGoalToProject(goalSlug: string, projectSlug: string): Promise<void> {
	const res = await fetch(`${BASE}/api/goals/${goalSlug}/projects/${projectSlug}`, {
		method: "POST",
	});
	await throwIfNotOk(res, "Failed to attach");
}

export async function detachGoalFromProject(goalSlug: string, projectSlug: string): Promise<void> {
	const res = await fetch(`${BASE}/api/goals/${goalSlug}/projects/${projectSlug}`, {
		method: "DELETE",
	});
	await throwIfNotOk(res, "Failed to detach");
}

// --- Goal shaping ---

export interface ShapeProposals {
	relevant_projects: Array<{ slug: string; title: string; reason: string; confidence: number }>;
	new_project: { title: string; description: string } | null;
	draft_tasks: Array<{ title: string; description: string; project_hint: string }>;
	success_metric: { text: string; rationale: string } | null;
	suggested_team: Array<{ agent: string; reason: string }>;
}

export interface GoalShapeResponse {
	input_completeness?: {
		truncated: boolean;
		sources: Array<{ source: string; shown_chars: number; original_chars: number; omitted_chars: number }>;
	};
	goal_slug: string;
	correlation_id: string;
	proposals: ShapeProposals;
	mutations: Record<string, number>;
}

export async function shapeGoal(slug: string): Promise<GoalShapeResponse> {
	const res = await fetch(`${BASE}/api/goals/${slug}/shape`, {
		method: "POST",
	});
	if (res.status === 409) {
		throw new Error("Goal shaping is disabled");
	}
	if (res.status === 503) {
		throw new Error("Shaper produced no output — try again");
	}
	if (res.status === 422) {
		throw new Error("Shaper returned unparseable output — try again");
	}
	if (res.status === 504) {
		throw new Error("Shaper timed out — try again");
	}
	await throwIfNotOk(res, "Failed to shape goal");
	return res.json();
}

// --- Pending questions (Ship 3 autonomous pursuit) ---

export interface GoalQuestion {
	id: string;
	question: string;
	asked_at: string;
	importance: "low" | "medium" | "high";
	context: string;
	// Answered questions also carry:
	answer?: string;
	answered_at?: string;
	// Dashboard endpoint enriches with:
	goal_slug?: string;
	goal_title?: string;
}

export async function fetchGoalQuestions(slug: string): Promise<{
	pending: GoalQuestion[];
	answered: GoalQuestion[];
}> {
	const res = await fetch(`${BASE}/api/goals/${slug}/questions`);
	await throwIfNotOk(res, "Failed to fetch goal questions");
	return res.json();
}

export async function fetchAllPendingQuestions(): Promise<GoalQuestion[]> {
	const res = await fetch(`${BASE}/api/goals/questions/pending`);
	await throwIfNotOk(res, "Failed to fetch pending questions");
	return res.json();
}

export async function answerGoalQuestion(
	slug: string,
	questionId: string,
	answer: string,
): Promise<GoalQuestion> {
	const res = await fetch(
		`${BASE}/api/goals/${slug}/questions/${questionId}/answer`,
		{
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ answer }),
		},
	);
	await throwIfNotOk(res, "Failed to answer question");
	return res.json();
}

export async function startGoalPursuit(slug: string): Promise<{ slug: string; pursuit_status: string }> {
	const res = await fetch(`${BASE}/api/goals/${slug}/pursuit/start`, {
		method: "POST",
	});
	await throwIfNotOk(res, "Failed to start pursuit");
	return res.json();
}

// --- Ship 4 PDCA: metric observations, retrospectives, milestones ---

export interface MetricObservation {
	id: string;
	value: string;
	numeric_value: number | null;
	observed_at: string;
	source: string;
	note: string;
}

export interface MetricProgress {
	target: number | null;
	unit: string;
	latest: string | null;
	latest_numeric?: number | null;
	latest_at: string | null;
	percent: number | null;
	observations_count: number;
	trend: "up" | "down" | "flat" | "unknown";
}

export async function fetchMetricProgress(slug: string): Promise<{
	progress: MetricProgress;
	observations: MetricObservation[];
}> {
	const res = await fetch(`${BASE}/api/goals/${slug}/metric`);
	await throwIfNotOk(res, "Failed to fetch metric progress");
	return res.json();
}

export async function recordMetricObservation(
	slug: string,
	value: string,
	opts: { numericValue?: number | null; source?: string; note?: string } = {},
): Promise<MetricObservation> {
	const res = await fetch(`${BASE}/api/goals/${slug}/metric/observation`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({
			value,
			numeric_value: opts.numericValue ?? null,
			source: opts.source ?? "manual",
			note: opts.note ?? "",
		}),
	});
	await throwIfNotOk(res, "Failed to record observation");
	return res.json();
}

export interface Retrospective {
	id: string;
	at: string;
	dispatch_count: number;
	verdict: "on_track" | "off_track" | "stuck" | "done";
	insights: string;
	recommended_action: "continue" | "pivot" | "ask_owner" | "pause" | "mark_achieved";
	recommendation_note: string;
	applied: "accepted" | "rejected" | null;
	applied_at?: string;
	owner_note?: string;
}

export async function fetchRetrospectives(slug: string): Promise<Retrospective[]> {
	const res = await fetch(`${BASE}/api/goals/${slug}/retrospectives`);
	await throwIfNotOk(res, "Failed to fetch retrospectives");
	return res.json();
}

export async function applyRetrospective(
	slug: string,
	retroId: string,
	accept: boolean,
	note = "",
): Promise<Retrospective> {
	const res = await fetch(
		`${BASE}/api/goals/${slug}/retrospectives/${retroId}/apply`,
		{
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ accept, note }),
		},
	);
	await throwIfNotOk(res, "Failed to apply retrospective decision");
	return res.json();
}

export interface Milestone {
	id: string;
	text: string;
	done: boolean;
	created_at: string;
	completed_at: string | null;
	source: string;
}

export interface MilestonesProgress {
	total: number;
	done: number;
	percent: number | null;
}

export async function fetchMilestones(slug: string): Promise<{
	milestones: Milestone[];
	progress: MilestonesProgress;
}> {
	const res = await fetch(`${BASE}/api/goals/${slug}/milestones`);
	await throwIfNotOk(res, "Failed to fetch milestones");
	return res.json();
}

export async function addMilestone(slug: string, text: string): Promise<Milestone> {
	const res = await fetch(`${BASE}/api/goals/${slug}/milestones`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ text, source: "owner" }),
	});
	await throwIfNotOk(res, "Failed to add milestone");
	return res.json();
}

export async function completeMilestone(
	slug: string,
	milestoneId: string,
	done = true,
): Promise<Milestone> {
	const res = await fetch(
		`${BASE}/api/goals/${slug}/milestones/${milestoneId}/complete`,
		{
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ done }),
		},
	);
	await throwIfNotOk(res, "Failed to update milestone");
	return res.json();
}

export async function deleteMilestone(slug: string, milestoneId: string): Promise<void> {
	const res = await fetch(
		`${BASE}/api/goals/${slug}/milestones/${milestoneId}`,
		{ method: "DELETE" },
	);
	await throwIfNotOk(res, "Failed to delete milestone");
}

export async function fetchTask(slug: string, project?: string): Promise<TaskDetail> {
	const res = await fetch(`${BASE}/api/tasks/${encodeURIComponent(slug)}${taskProjectQuery(project)}`);
	await throwIfNotOk(res, "Failed to fetch task");
	return res.json();
}

export async function updateTaskStatus(slug: string, status: string, project?: string, before?: { slug: string; project: string } | null): Promise<void> {
	const res = await fetch(`${BASE}/api/tasks/${encodeURIComponent(slug)}/status${taskProjectQuery(project)}`, {
		method: "PATCH",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ status, before }),
	});
	await throwIfNotOk(res, "Failed to update task");
}

export async function submitTaskFeedback(
	slug: string,
	rating: string,
	note = "",
	project?: string,
): Promise<{ trust_update: { domain: string; old_level: string; new_level: string; changed: boolean } }> {
	const res = await fetch(`${BASE}/api/tasks/${encodeURIComponent(slug)}/feedback${taskProjectQuery(project)}`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ rating, note }),
	});
	await throwIfNotOk(res, "Failed to submit feedback");
	return res.json();
}

export async function deleteTask(slug: string, project?: string): Promise<{ slug: string; project: string; status: string }> {
	const res = await fetch(`${BASE}/api/tasks/${encodeURIComponent(slug)}${taskProjectQuery(project)}`, { method: "DELETE" });
	await throwIfNotOk(res, "Failed to delete task");
	return res.json();
}

export async function addTaskProgress(slug: string, note: string, project?: string): Promise<void> {
	const res = await fetch(`${BASE}/api/tasks/${encodeURIComponent(slug)}/progress${taskProjectQuery(project)}`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ note }),
	});
	await throwIfNotOk(res, "Failed to add progress");
}

// --- Search ---

export interface SearchResult {
	path: string;
	title: string;
	snippet: string;
	score: number;
	route: string | null;
	action: "navigate" | "open_file";
	content_type: string;
}

export async function searchVault(query: string, limit = 10): Promise<SearchResult[]> {
	const res = await fetch(`${BASE}/api/search?q=${encodeURIComponent(query)}&limit=${limit}`);
	await throwIfNotOk(res, "Search failed");
	const body = await res.json();
	// Backend returns {items, total, has_more} per Rule 20; tolerate the
	// legacy bare-list shape for safety during a partial deploy.
	return Array.isArray(body) ? body : (body.items ?? []);
}

// Semantic + keyword hybrid search (70% vector + 30% keyword).
// Raw shape from the backend differs from the command-palette SearchResult
// — `path` + `title` + `snippet` + `score`, no `route` / `action` fields.
export interface HybridSearchHit {
	path: string;
	title?: string;
	snippet?: string;
	score?: number;
	[extra: string]: unknown;
}

export async function searchHybrid(query: string, limit = 5): Promise<HybridSearchHit[]> {
	const res = await fetch(
		`${BASE}/api/search/hybrid?q=${encodeURIComponent(query)}&limit=${limit}`,
	);
	if (!res.ok) return [];  // related-notes is best-effort — never block the UI
	try {
		const body = await res.json();
		// Backend returns {items, total, has_more} per Rule 20; tolerate the
		// legacy bare-list shape for safety during a partial deploy.
		const items = Array.isArray(body) ? body : (body.items ?? []);
		return items as HybridSearchHit[];
	} catch {
		return [];
	}
}

// Cross-conversation message search (Cmd+K palette Messages section).
// Backend queries the messages table directly, so it works regardless of the
// opt-in vault_fts message index. Each hit deep-links to
// /chat/{conversation_id}?msg={message_id}.
export interface MessageSearchResult {
	conversation_id: string;
	conversation_title: string;
	message_id: string;
	role: string;
	snippet: string;
	created_at: string;
}

export async function searchMessages(
	query: string,
	limit = 8,
): Promise<MessageSearchResult[]> {
	const res = await fetch(
		`${BASE}/api/search/messages?q=${encodeURIComponent(query)}&limit=${limit}`,
	);
	if (!res.ok) return []; // palette section is best-effort — never block Cmd+K
	try {
		const body = await res.json();
		const items = Array.isArray(body) ? body : (body.items ?? []);
		return items as MessageSearchResult[];
	} catch {
		return [];
	}
}

// --- Reflections ---

export interface ReflectionSummary {
	path: string;
	title: string;
	status: string;
}

export async function fetchReflections(limit = 20): Promise<ReflectionSummary[]> {
	const res = await fetch(`${BASE}/api/reflections?limit=${limit}`);
	await throwIfNotOk(res, "Failed to fetch reflections");
	const body = await res.json();
	return Array.isArray(body) ? body : (body.items ?? []);
}

export async function runDailyReflection(date?: string): Promise<{ date: string; reflection: string; status: string }> {
	const url = date ? `${BASE}/api/reflections/daily?date=${date}` : `${BASE}/api/reflections/daily`;
	const res = await fetch(url, { method: "POST" });
	await throwIfNotOk(res, "Failed to run reflection");
	return res.json();
}

export async function runJournalFillWorkflow(): Promise<{ status: string; message: string }> {
	const res = await fetch(`${BASE}/api/daily/run-workflow`, { method: "POST" });
	await throwIfNotOk(res, "Failed to start fill workflow");
	return res.json();
}

export async function approveReflection(path: string, editedContent?: string): Promise<void> {
	const opts: RequestInit = { method: "POST" };
	if (editedContent !== undefined) {
		opts.headers = { "Content-Type": "application/json" };
		opts.body = JSON.stringify({ content: editedContent });
	}
	const res = await fetch(`${BASE}/api/reflections/${path}/approve`, opts);
	await throwIfNotOk(res, "Failed to approve");
}

export async function rejectReflection(path: string, reason?: string): Promise<void> {
	const res = await fetch(`${BASE}/api/reflections/${path}/reject`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ reason: reason ?? "" }),
	});
	await throwIfNotOk(res, "Failed to reject");
}

// ─── Per-file provenance (audit trail) ───────────────────────────────────

export interface ProvenanceModelUsage {
	model: string;
	provider: string | null;
	tier: string | null;
	mode: string | null;
	calls: number;
	input_tokens: number;
	output_tokens: number;
}

export interface ProvenanceDecision {
	ts: string;
	actor: string | null;
	action_type: string | null;
	action_target: string | null;
	contract_action: string | null;
	contract_result: string | null;
	rationale: string;
	skills: string[];
	trust_level: string | null;
	trust_domain: string | null;
	// Compaction runs stamp their input files (dict of unit → paths).
	source_files: Record<string, string[]> | null;
}

export interface ProvenanceItem {
	id: number;
	written_at: string;
	action: string; // "create" | "modify"
	correlation_id: string;
	history_version: string | null;
	actors: string[];
	skills: string[];
	rationale: string;
	action_type: string | null;
	model_recorded: boolean;
	models: ProvenanceModelUsage[];
	decisions: ProvenanceDecision[];
	decision_total: number;
	activity: { ts: string; category: string; action: string; summary: string; importance: string }[];
	activity_total: number;
}

export interface ProvenancePage {
	path: string;
	items: ProvenanceItem[];
	total: number;
	has_more: boolean;
}

export async function fetchFileProvenance(
	path: string,
	limit = 20,
	offset = 0,
): Promise<ProvenancePage> {
	const params = new URLSearchParams({
		path,
		limit: String(limit),
		offset: String(offset),
	});
	const res = await fetch(`${BASE}/api/provenance/file?${params}`);
	await throwIfNotOk(res, "Failed to load provenance");
	const body = await res.json();
	return {
		path: body.path ?? path,
		items: body.items ?? [],
		total: body.total ?? 0,
		has_more: !!body.has_more,
	};
}

// ─── Memory approval ─────────────────────────────────────────────────────

export async function fetchPendingMemoryContent(date: string): Promise<string> {
	const res = await fetch(`${BASE}/api/memory/pending/${date}/content`);
	await throwIfNotOk(res, "Failed to load pending content");
	const data = await res.json();
	return data.content ?? "";
}

export async function approveMemoryUpdate(
	date: string,
	editedContent?: string,
	rejectedBullets?: string[],
): Promise<void> {
	const opts: RequestInit = { method: "POST" };
	const hasVeto = rejectedBullets !== undefined && rejectedBullets.length > 0;
	if (editedContent !== undefined || hasVeto) {
		opts.headers = { "Content-Type": "application/json" };
		const body: Record<string, unknown> = {};
		if (editedContent !== undefined) body.content = editedContent;
		if (hasVeto) body.rejected_bullets = rejectedBullets;
		opts.body = JSON.stringify(body);
	}
	const res = await fetch(`${BASE}/api/memory/pending/${date}/approve`, opts);
	await throwIfNotOk(res, "Failed to approve");
}

// ─── Memory corrections ledger ───────────────────────────────────────────

export interface MemoryCorrection {
	id: string;
	date: string;
	claim: string;
	truth: string;
	retracted: boolean;
}

export async function fetchMemoryCorrections(
	includeRetracted = false,
): Promise<{ items: MemoryCorrection[]; total: number; has_more: boolean }> {
	const res = await fetch(`${BASE}/api/memory/corrections?include_retracted=${includeRetracted}`);
	await throwIfNotOk(res, "Failed to load corrections");
	return res.json();
}

export async function addMemoryCorrection(
	claim: string,
	truth?: string,
): Promise<{ status: string; correction: MemoryCorrection; cascade: string }> {
	const res = await fetch(`${BASE}/api/memory/corrections`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ claim, truth: truth ?? "" }),
	});
	await throwIfNotOk(res, "Failed to record correction");
	return res.json();
}

export async function retractMemoryCorrection(id: string): Promise<void> {
	const res = await fetch(`${BASE}/api/memory/corrections/${id}/retract`, { method: "POST" });
	await throwIfNotOk(res, "Failed to retract correction");
}

export async function editMemorySummary(
	layer: string,
	content: string,
): Promise<{ status: string; durable: boolean }> {
	const res = await fetch(`${BASE}/api/memory/summaries/${layer}`, {
		method: "PUT",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ content }),
	});
	await throwIfNotOk(res, "Failed to save summary");
	return res.json();
}

export async function rejectMemoryUpdate(date: string, reason?: string): Promise<void> {
	const res = await fetch(`${BASE}/api/memory/pending/${date}/reject`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ reason: reason ?? "" }),
	});
	await throwIfNotOk(res, "Failed to reject");
}

// ─── Dream review ─────────────────────────────────────────────────────

export async function confirmDreamBelief(
	id: number,
	edits?: { subject?: string; predicate?: string; object?: string },
): Promise<void> {
	const res = await fetch(`${BASE}/api/memory/dream-review/${id}/confirm`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(edits ?? {}),
	});
	await throwIfNotOk(res, "Failed to confirm belief");
}

export async function rejectDreamBelief(id: number, reason: string): Promise<void> {
	const res = await fetch(`${BASE}/api/memory/dream-review/${id}/reject`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ reason }),
	});
	await throwIfNotOk(res, "Failed to reject belief");
}

export async function unapproveMemoryUpdate(date: string): Promise<void> {
	const res = await fetch(`${BASE}/api/memory/pending/${date}/unapprove`, { method: "POST" });
	if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Unapprove failed");
}

export async function batchApproveMemory(dates?: string[]): Promise<{approved: number; skipped: number}> {
	const res = await fetch(`${BASE}/api/memory/pending/batch-approve`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(dates ? { dates } : {}),
	});
	if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Batch approve failed");
	return res.json();
}

export async function batchConfirmDreamBeliefs(tripleIds?: number[]): Promise<{confirmed: number; skipped: number}> {
	const res = await fetch(`${BASE}/api/memory/dream-review/batch-confirm`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(tripleIds ? { triple_ids: tripleIds } : {}),
	});
	if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Batch confirm failed");
	return res.json();
}

export async function fetchReviewHistory(surface?: string, limit = 50): Promise<Array<{ts: string; surface: string; item_id: string; action: string; reason: string}>> {
	const params = new URLSearchParams();
	if (surface) params.set("surface", surface);
	params.set("limit", String(limit));
	const res = await fetch(`${BASE}/api/memory/review-history?${params}`);
	if (!res.ok) throw new Error("Failed to load review history");
	return res.json();
}

// ─── Relationship evolution ─────────────────────────────────────────

export async function approveRelationshipEvolution(editedContent?: string): Promise<void> {
	const opts: RequestInit = { method: "POST" };
	if (editedContent !== undefined) {
		opts.headers = { "Content-Type": "application/json" };
		opts.body = JSON.stringify({ content: editedContent });
	}
	const res = await fetch(`${BASE}/api/relationship/pending/approve`, opts);
	await throwIfNotOk(res, "Failed to approve evolution");
}

// ── Persona switching (Option A, 2026-05-06) ──────────────────────────────

export interface PersonaState {
	active: string;
	available: string[];
	base_in_effect: boolean;
}

export async function fetchPersona(): Promise<PersonaState> {
	const res = await fetch(`${BASE}/api/persona`);
	await throwIfNotOk(res, "Failed to load persona state");
	return res.json();
}

export async function fetchPersonaVariant(name: string): Promise<{ name: string; body: string }> {
	const res = await fetch(`${BASE}/api/persona/${encodeURIComponent(name)}`);
	await throwIfNotOk(res, `Failed to load variant '${name}'`);
	return res.json();
}

export async function switchPersona(
	name: string,
): Promise<{ switched: boolean; active: string; available: string[]; message: string }> {
	const res = await fetch(`${BASE}/api/persona/switch`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ name }),
	});
	if (!res.ok) {
		const detail = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }));
		throw new Error(detail.detail || `Switch failed (HTTP ${res.status})`);
	}
	return res.json();
}

export async function resetPersona(): Promise<{
	reset: boolean;
	active: string;
	available: string[];
	message: string;
}> {
	const res = await fetch(`${BASE}/api/persona/reset`, { method: "POST" });
	await throwIfNotOk(res, "Failed to reset persona");
	return res.json();
}

export async function rejectRelationshipEvolution(reason?: string): Promise<void> {
	const res = await fetch(`${BASE}/api/relationship/pending/reject`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ reason: reason ?? "" }),
	});
	await throwIfNotOk(res, "Failed to reject evolution");
}

// ─── Error-reporting fetch wrapper ─────────────────────────────────────────
//
// Use `apiCall` for any mutation that the user initiated. On failure it emits
// a toast with the user-facing error text so the action never "does nothing".
// For reads where a silent fallback is acceptable (e.g. optional side-panel
// data), keep using raw fetch. The rule: if the user clicked it, wrap it.

import { toast } from "../stores/toast";

export interface ApiCallOptions {
	/** Human-facing title for the error toast. Default: "Request failed". */
	errorTitle?: string;
	/** If set, push a success toast with this title on 2xx. */
	successTitle?: string;
	/** Request init (method, body, headers). */
	init?: RequestInit;
}

export class ApiError extends Error {
	status: number;
	url: string;
	detail?: string;
	constructor(message: string, status: number, url: string, detail?: string) {
		super(message);
		this.name = "ApiError";
		this.status = status;
		this.url = url;
		this.detail = detail;
	}
}

async function extractErrorDetail(res: Response): Promise<string> {
	try {
		const data = await res.clone().json();
		if (typeof data === "string") return data;
		if (data && typeof data === "object") {
			if (typeof data.detail === "string") return data.detail;
			if (typeof data.message === "string") return data.message;
			if (typeof data.error === "string") return data.error;
		}
	} catch {
		try {
			const text = await res.clone().text();
			if (text) return text.slice(0, 200);
		} catch {
			/* ignore */
		}
	}
	return `HTTP ${res.status}`;
}

/**
 * Fetch wrapper that surfaces errors to the user via toast. Returns an
 * `ApiResult<T>` so callers can branch on `ok` without a try/catch. On
 * failure the user has already seen the error toast.
 *
 * Example:
 *   const res = await apiCall<{ slug: string }>("/api/kb", {
 *     init: { method: "POST", body: JSON.stringify({ title: "Research" }) },
 *     errorTitle: "Could not create knowledge base",
 *     successTitle: "Knowledge base created",
 *   });
 *   if (res.ok) refreshKBs();
 */
export interface ApiResult<T> {
	ok: boolean;
	data: T | null;
	status: number;
}

export async function apiCall<T = unknown>(
	url: string,
	opts: ApiCallOptions = {},
): Promise<ApiResult<T>> {
	const { errorTitle = "Request failed", successTitle, init } = opts;
	try {
		const res = await fetch(url, init);
		if (!res.ok) {
			const detail = await extractErrorDetail(res);
			toast.error(errorTitle, detail);
			return { ok: false, data: null, status: res.status };
		}
		if (successTitle) toast.success(successTitle);
		if (res.status === 204) return { ok: true, data: null, status: 204 };
		const text = await res.text();
		if (!text) return { ok: true, data: null, status: res.status };
		try {
			return { ok: true, data: JSON.parse(text) as T, status: res.status };
		} catch {
			return { ok: true, data: null, status: res.status };
		}
	} catch (err) {
		const message = err instanceof Error ? err.message : String(err);
		toast.error(errorTitle, message);
		return { ok: false, data: null, status: 0 };
	}
}

// ── Vault file browser (Files sidebar mode) ───────────────────────────────

export interface BrowseEntry {
	name: string;
	path: string;
	kind: "dir" | "file";
	size: number;
	modified: string;
	is_hidden: boolean;
}

export interface BrowseListing {
	root: string;
	path: string;
	parent: string | null;
	entries: BrowseEntry[];
}

export type BrowseRead =
	| { text: string; encoding: string; size: number; modified: string; path: string }
	| { binary: true; size: number; modified: string; path: string; reason: "too_large" | "binary" };

export async function browseVault(path: string = ""): Promise<BrowseListing> {
	const qs = path ? `?path=${encodeURIComponent(path)}` : "";
	const res = await fetch(`${BASE}/api/vault/browse${qs}`);
	await throwIfNotOk(res, "Failed to list folder");
	return res.json();
}

export async function readBrowseFile(path: string): Promise<BrowseRead> {
	const res = await fetch(`${BASE}/api/vault/browse/read?path=${encodeURIComponent(path)}`);
	await throwIfNotOk(res, "Failed to read file");
	const ct = res.headers.get("content-type") || "";
	// Images return raw bytes with image/* content-type; other preview
	// responses are JSON. The caller (preview modal) handles images via
	// the endpoint URL directly as an <img src="...">, so the JSON path
	// is the only one that reaches this function for images — no extra
	// case needed, but we surface a clear message if it does happen.
	if (ct.startsWith("image/")) {
		throw new Error("readBrowseFile: image path should be used as <img src> directly");
	}
	return res.json();
}

export function browseImageUrl(path: string): string {
	return `${BASE}/api/vault/browse/read?path=${encodeURIComponent(path)}`;
}

// ── Vault file management (2026-08-15) ─────────────────────────────────────
// Rename · move · new folder · trash (undoable) · restore · upload · purge.
// The server answers refusals with `{code, message}` (Rule 26 — a reason
// the user can act on: "already exists here", "cannot be moved into
// itself"), so these surface the MESSAGE rather than a bare status word.

export interface TrashEntry {
	trash_id: string;
	name: string;
	original_path: string;
	kind: "dir" | "file";
	size: number;
	trashed_at: string;
}

async function browseMutation<T>(res: Response, action: string): Promise<T> {
	if (res.ok) return res.json();
	let message = friendlyStatus(res.status);
	try {
		const body = await res.json();
		const d = body?.detail;
		if (d && typeof d === "object" && typeof d.message === "string") message = d.message;
		else if (typeof d === "string") message = d;
	} catch {
		/* keep the status text */
	}
	throw new Error(`${action}: ${message}`);
}

export async function createFolder(parent: string, name: string): Promise<{ path: string }> {
	const res = await fetch(`${BASE}/api/vault/browse/mkdir`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ parent, name }),
	});
	return browseMutation(res, "Couldn't create folder");
}

export async function renameEntry(path: string, newName: string): Promise<{ path: string }> {
	const res = await fetch(`${BASE}/api/vault/browse/rename`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ path, new_name: newName }),
	});
	return browseMutation(res, "Couldn't rename");
}

export async function moveEntry(path: string, destination: string): Promise<{ path: string }> {
	const res = await fetch(`${BASE}/api/vault/browse/move`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ path, destination }),
	});
	return browseMutation(res, "Couldn't move");
}

export async function trashEntry(path: string): Promise<{ trash_id: string }> {
	const res = await fetch(`${BASE}/api/vault/browse/trash`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ path }),
	});
	return browseMutation(res, "Couldn't move to trash");
}

export async function listTrash(): Promise<{ total: number; entries: TrashEntry[] }> {
	const res = await fetch(`${BASE}/api/vault/browse/trash`);
	return browseMutation(res, "Couldn't read the trash");
}

export async function restoreTrashEntry(trashId: string): Promise<{ path: string }> {
	const res = await fetch(`${BASE}/api/vault/browse/trash/restore`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ trash_id: trashId }),
	});
	return browseMutation(res, "Couldn't restore");
}

/** PERMANENT. Only ever called from behind a confirm dialog. */
export async function purgeTrashEntry(trashId: string): Promise<{ deleted: string }> {
	const res = await fetch(`${BASE}/api/vault/browse/trash/${encodeURIComponent(trashId)}`, {
		method: "DELETE",
	});
	return browseMutation(res, "Couldn't delete");
}

export async function uploadToFolder(destination: string, file: globalThis.File): Promise<{ path: string; size: number }> {
	const form = new FormData();
	form.append("file", file);
	const res = await fetch(
		`${BASE}/api/vault/browse/upload?destination=${encodeURIComponent(destination)}`,
		{ method: "POST", body: form },
	);
	return browseMutation(res, "Couldn't upload");
}

// ── Work queue polling (Phase 2 Session 5, 2026-05-15) ─────────────────────

/** Single work-queue job state returned by `GET /api/work-queue/{job_id}`.
 *
 * `terminal: true` means the job is in `completed` or `dead_letter`.
 * `result` is JSON-decoded when present; `null` is preserved when
 * the handler returned None on a successful job.
 */
export interface WorkQueueJob {
	id: number;
	job_type: string;
	payload: unknown;
	status: "pending" | "running" | "failed" | "completed" | "dead_letter";
	attempts: number;
	max_attempts: number;
	next_retry: string | null;
	created_at: string;
	updated_at: string;
	started_at: string | null;
	completed_at: string | null;
	error: string | null;
	result: unknown;
	last_heartbeat_at: string | null;
	worker_pid: number | null;
	worker_id: string | null;
	lease_until: string | null;
	terminal: boolean;
}

/** Fetch a single job's current state. Returns null on 404 (unknown id)
 * so callers can distinguish from a partial / corrupt response. Throws
 * on 5xx or network failure — the poll helper handles transient errors
 * by retrying. */
export async function getJob(jobId: number): Promise<WorkQueueJob | null> {
	const res = await fetch(`${BASE}/api/work-queue/${jobId}`);
	if (res.status === 404) return null;
	await throwIfNotOk(res, "Failed to fetch job state");
	return res.json();
}

export interface PollJobOptions {
	/** Poll cadence in milliseconds (default 2000). */
	intervalMs?: number;
	/** Total timeout in milliseconds (default 1800000 = 30 min). */
	timeoutMs?: number;
	/** Called on every successful poll with the current job state.
	 * The toast component uses this to render progress in real time. */
	onProgress?: (job: WorkQueueJob) => void;
	/** AbortSignal — when triggered, polling stops cleanly without
	 * throwing. The promise resolves to the last-seen state OR null
	 * if no state was ever fetched. */
	signal?: AbortSignal;
}

/** Poll `/api/work-queue/{jobId}` until terminal, abort, or timeout.
 *
 * Phase 2 Session 5 (2026-05-15) — the canonical helper that every
 * async-route UI consumer uses to track a fire-and-forget job.
 * Replaces the bespoke poll loops that each "long-running operation"
 * page implemented separately (e.g. GraphPageV2 polling
 * `/api/graph/stats` for `total_nodes > 0`).
 *
 * Behavior contract:
 * - Returns the final `WorkQueueJob` on terminal state (success OR
 *   dead-letter). Callers branch on `result.status` / `result.error`.
 * - Transient fetch errors are swallowed at debug — keep polling
 *   until timeout. A genuinely-broken job will surface via
 *   `dead_letter` once the worker exhausts retries.
 * - Returns `null` if the job is deleted mid-poll (404 after at
 *   least one successful fetch).
 * - Aborted polls resolve to the last-seen state (or null if no
 *   poll completed before abort).
 * - Throws `Error("timeout")` if the timeout window elapses without
 *   reaching a terminal state.
 */
export async function pollJob(
	jobId: number,
	opts: PollJobOptions = {}
): Promise<WorkQueueJob | null> {
	const interval = opts.intervalMs ?? 2000;
	const timeout = opts.timeoutMs ?? 30 * 60 * 1000;
	const start = Date.now();
	let lastSeen: WorkQueueJob | null = null;

	while (Date.now() - start < timeout) {
		if (opts.signal?.aborted) {
			return lastSeen;
		}
		try {
			const job = await getJob(jobId);
			if (job === null) {
				// 404. If we had seen the job before, it was deleted
				// mid-poll — return null to signal "gone". If we
				// never saw it, this is the first poll; the route
				// might be a race with enqueue commit, so keep
				// polling for a few more cycles.
				if (lastSeen !== null) return null;
			} else {
				lastSeen = job;
				if (opts.onProgress) {
					try {
						opts.onProgress(job);
					} catch {
						// Progress callback errors must never break
						// the poll loop — the caller's responsibility.
					}
				}
				if (job.terminal) return job;
			}
		} catch (err) {
			// Transient fetch error (network blip, 5xx). Keep going —
			// the work queue itself owns retry semantics; we just
			// surface whatever final state lands.
			console.debug("[pollJob] transient fetch error", err);
		}

		// Wait — but honor abort signal during the sleep so a cancel
		// from the toast component lands within `interval` ms.
		await new Promise<void>((resolve) => {
			const timer = window.setTimeout(resolve, interval);
			if (opts.signal) {
				opts.signal.addEventListener(
					"abort",
					() => {
						window.clearTimeout(timer);
						resolve();
					},
					{ once: true }
				);
			}
		});
	}

	throw new Error("timeout");
}

// ────────────────────────────────────────────────────────────────────
// Career — the RETIRED six-file facts model (status + one-time import)
// ────────────────────────────────────────────────────────────────────
//
// Facts are per-item files (see the items block below) — consolidated
// 2026-08-16. These calls exist only so the Facts page can show a
// pre-consolidation vault its old files and import them.

export interface CareerLegacyFactMeta {
	category: string;
	path: string;
	exists: boolean;
	size_bytes: number;
	last_updated: string;
	is_empty: boolean;
	/** Not a pristine template — the user typed something into it. */
	has_user_content: boolean;
	/** Set once the file was imported into facts (items). */
	migrated_at: string;
}

export interface CareerLegacyFactsResponse {
	items: CareerLegacyFactMeta[];
	total: number;
	has_more: boolean;
	/** Files with user content not yet imported — the import banner count. */
	pending_import: number;
}

export const CAREER_LEGACY_FACT_LABELS: Record<string, string> = {
	"work-history": "Work history",
	education: "Education",
	skills: "Skills",
	projects: "Notable projects",
	achievements: "Achievements",
	recommendations: "Recommendations",
};

export async function fetchCareerLegacyFacts(): Promise<CareerLegacyFactsResponse> {
	const res = await fetch(`${BASE}/api/career/facts`);
	await throwIfNotOk(res, "Failed to fetch older facts files");
	return res.json();
}

export async function fetchCareerLegacyFact(category: string): Promise<{
	category: string; path: string; metadata: Record<string, unknown>; content: string; is_empty: boolean;
}> {
	const res = await fetch(`${BASE}/api/career/facts/${encodeURIComponent(category)}`);
	await throwIfNotOk(res, "Failed to fetch older facts file");
	return res.json();
}

export interface CareerLegacyImportPlan {
	migrated: boolean;
	plan: Record<string, { target_category: string; count: number; items: { title: string; body_preview: string; status: string; slug?: string }[] }>;
	created_total: number;
	skipped_template_only: string[];
	skipped_already_migrated: string[];
	stamped: string[];
	errors: string[];
}

/** ``POST /api/career/items/migrate-from-facts`` — dry run unless confirm. */
export async function importCareerLegacyFacts(payload: {
	confirm?: boolean; categories?: string[]; force?: boolean;
} = {}): Promise<CareerLegacyImportPlan> {
	const res = await fetch(`${BASE}/api/career/items/migrate-from-facts`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(payload),
	});
	await throwIfNotOk(res, "Failed to import older facts files");
	return res.json();
}

/** ``GET /api/career/summary`` — the ONE aggregation the dashboard, the agent
 * (`career_summary`) and the CLI share, so the facts count a user sees is
 * the one the agent quotes. */
export interface CareerSummary {
	listings: { total: number; by_status: Record<string, number>; oldest: string; newest: string; recent: unknown[] };
	applications: { total: number; by_status: Record<string, number>; oldest: string; newest: string; recent: unknown[] };
	/** Facts by category — the API key says "items"; the words are "facts". */
	items_by_category: Record<string, number>;
	items_total: number;
	facts_newest_update: string;
	legacy_facts_pending_import: number;
}

export async function fetchCareerSummary(): Promise<CareerSummary> {
	const res = await fetch(`${BASE}/api/career/summary`);
	await throwIfNotOk(res, "Failed to fetch career summary");
	return res.json();
}

// ────────────────────────────────────────────────────────────────────
// Career subsystem — Phase 4: item-based facts (modular rebuild)
// ────────────────────────────────────────────────────────────────────

export interface CareerCategoryInfo {
	slug: string;
	label: string;
	description: string;
	is_default: boolean;
	suggested_fields: string[];
}

export interface CareerCategoriesResponse {
	items: CareerCategoryInfo[];
	total: number;
	has_more: boolean;
}

export interface CareerItemMeta {
	category: string;
	slug: string;
	title: string;
	path: string;
	tags: string[];
	relevance_hints: string[];
	display_order: number;
	updated_at: string;
	size_bytes: number;
	organization?: string;
	start_date?: string;
	end_date?: string;
	current?: boolean;
	subcategory?: string;
	proficiency?: string;
	summary_excerpt?: string;
}

export interface CareerItemsListResponse {
	items: CareerItemMeta[];
	total: number;
	has_more: boolean;
	by_category: Record<string, number>;
}

export interface CareerItemDetail {
	category: string;
	slug: string;
	path: string;
	metadata: Record<string, unknown>;
	content: string;
}

export interface CareerItemCreateResponse {
	category: string;
	slug: string;
	path: string;
	created_at: string;
}

export interface CareerSelectedItem {
	category: string;
	slug: string;
}

export interface CareerSuggestion {
	category: string;
	slug: string;
	title: string;
	score: number;
	reasons: string[];
	display_order: number;
}

export interface CareerSuggestResponse {
	slug: string;
	suggestions: CareerSuggestion[];
	gap_present: boolean;
	by_category: Record<string, number>;
	reason?: string;
}

export async function fetchCareerCategories(): Promise<CareerCategoriesResponse> {
	const res = await fetch(`${BASE}/api/career/categories`);
	await throwIfNotOk(res, "Failed to fetch career categories");
	return res.json();
}

export async function registerCareerCategory(payload: {
	slug: string;
	label: string;
	description?: string;
	suggested_fields?: string[];
}): Promise<CareerCategoryInfo> {
	const res = await fetch(`${BASE}/api/career/categories`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(payload),
	});
	await throwIfNotOk(res, "Failed to register category");
	return res.json();
}

export async function unregisterCareerCategory(slug: string): Promise<{ removed: boolean; slug: string }> {
	const res = await fetch(`${BASE}/api/career/categories/${encodeURIComponent(slug)}`, {
		method: "DELETE",
	});
	await throwIfNotOk(res, "Failed to unregister category");
	return res.json();
}

export async function fetchCareerItems(opts: {
	category?: string;
	tags?: string;
	search?: string;
	limit?: number;
	offset?: number;
} = {}): Promise<CareerItemsListResponse> {
	const qs = new URLSearchParams();
	if (opts.category) qs.set("category", opts.category);
	if (opts.tags) qs.set("tags", opts.tags);
	if (opts.search) qs.set("search", opts.search);
	if (opts.limit !== undefined) qs.set("limit", String(opts.limit));
	if (opts.offset !== undefined) qs.set("offset", String(opts.offset));
	const url = `${BASE}/api/career/items${qs.toString() ? `?${qs}` : ""}`;
	const res = await fetch(url);
	await throwIfNotOk(res, "Failed to fetch career items");
	return res.json();
}

export async function fetchCareerItem(category: string, slug: string): Promise<CareerItemDetail> {
	const res = await fetch(
		`${BASE}/api/career/items/${encodeURIComponent(category)}/${encodeURIComponent(slug)}`,
	);
	await throwIfNotOk(res, "Failed to fetch career item");
	return res.json();
}

export async function createCareerItem(payload: {
	category: string;
	title: string;
	body?: string;
	frontmatter?: Record<string, unknown>;
}): Promise<CareerItemCreateResponse> {
	const res = await fetch(`${BASE}/api/career/items`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(payload),
	});
	await throwIfNotOk(res, "Failed to create career item");
	return res.json();
}

export async function updateCareerItem(
	category: string,
	slug: string,
	payload: {
		title?: string;
		body?: string;
		frontmatter?: Record<string, unknown>;
		merge?: boolean;
	},
): Promise<{ category: string; slug: string; path: string; updated_fields: string[] }> {
	const res = await fetch(
		`${BASE}/api/career/items/${encodeURIComponent(category)}/${encodeURIComponent(slug)}`,
		{
			method: "PATCH",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(payload),
		},
	);
	await throwIfNotOk(res, "Failed to update career item");
	return res.json();
}

export async function deleteCareerItem(category: string, slug: string): Promise<{
	deleted: boolean;
	category: string;
	slug: string;
	reason?: string;
}> {
	const res = await fetch(
		`${BASE}/api/career/items/${encodeURIComponent(category)}/${encodeURIComponent(slug)}`,
		{ method: "DELETE" },
	);
	await throwIfNotOk(res, "Failed to delete career item");
	return res.json();
}

// ── CV import (Word/PDF → per-item preview → user-curated commit) ──────

export interface CVImportItem {
	category: string;
	title: string;
	body: string;
	frontmatter: Record<string, unknown>;
}

export interface CVImportPreviewResponse {
	items: Record<string, CVImportItem[]>;
	reason: string;
	warnings: string[];
	raw_text_length: number;
	truncated: boolean;
	total_items: number;
	raw_output_preview: string;
	raw_output_length: number;
}

export interface CVImportCommitResponse {
	created: Array<{ category: string; slug: string; path: string; created_at: string }>;
	errors: Array<{ title: string; category: string; reason: string; message: string }>;
	skipped: Array<{ title: string; category: string; slug: string; reason: string }>;
	created_count: number;
	error_count: number;
	skipped_count: number;
	total_requested: number;
}

// Preview is an ASYNC JOB — per-category extraction takes multiple LLM
// calls (minutes on a local model). This kicks off the job and returns a
// job_id; poll GET /api/jobs/{id} (via <AsyncJobProgress>) for live
// progress, then read the terminal snapshot's `result` (a
// CVImportPreviewResponse) to render the preview.
export interface CVImportJobStart {
	job_id: string;
	kind: string;
}

export async function previewCVImport(file: File): Promise<CVImportJobStart> {
	const form = new FormData();
	form.append("file", file);
	const res = await fetch(`${BASE}/api/career/items/import-cv/preview`, {
		method: "POST",
		body: form,
	});
	await throwIfNotOk(res, "Failed to start CV import");
	return res.json();
}

export async function commitCVImport(
	items: CVImportItem[],
): Promise<CVImportCommitResponse> {
	const res = await fetch(`${BASE}/api/career/items/import-cv/commit`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ items }),
	});
	await throwIfNotOk(res, "Failed to import CV items");
	return res.json();
}

export async function setApplicationSelectedItems(
	slug: string,
	items: CareerSelectedItem[],
): Promise<{ slug: string; path: string; selected_items: CareerSelectedItem[]; count: number }> {
	const res = await fetch(
		`${BASE}/api/career/applications/${encodeURIComponent(slug)}/selected-items`,
		{
			method: "PUT",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ items }),
		},
	);
	await throwIfNotOk(res, "Failed to set selected items");
	return res.json();
}

export async function suggestItemsForApplication(slug: string): Promise<CareerSuggestResponse> {
	const res = await fetch(
		`${BASE}/api/career/applications/${encodeURIComponent(slug)}/items/suggest`,
	);
	await throwIfNotOk(res, "Failed to fetch item suggestions");
	return res.json();
}

/** AI relevance score for one item: 0-100 + a one-line reason. */
export interface CareerAiScore {
	score: number;
	reason: string;
}

export interface CareerScoreRelevanceResponse {
	ok: boolean;
	slug: string;
	reason?: string;
	message?: string;
	count?: number;
	/** Map of "category/slug" → {score, reason}. */
	scores?: Record<string, CareerAiScore>;
}

/** Run semantic AI relevance scoring (one local-LLM call) for an
 * application's items against its JD. Results are cached server-side on
 * the application metadata. */
export async function scoreApplicationRelevance(
	slug: string,
): Promise<CareerScoreRelevanceResponse> {
	const res = await fetch(
		`${BASE}/api/career/applications/${encodeURIComponent(slug)}/score-relevance`,
		{ method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" },
	);
	await throwIfNotOk(res, "Failed to score relevance");
	return res.json();
}

// ────────────────────────────────────────────────────────────────────
// Career subsystem — Phase 2: listings CRUD
// ────────────────────────────────────────────────────────────────────
export const LISTING_STATUSES = [
	"saved",
	"applied",
	"interviewing",
	"offer",
	"rejected",
	"declined",
	"withdrawn",
] as const;
export type ListingStatus = (typeof LISTING_STATUSES)[number];

export const LISTING_STATUS_LABELS: Record<ListingStatus, string> = {
	saved: "Saved",
	applied: "Applied",
	interviewing: "Interviewing",
	offer: "Offer",
	rejected: "Rejected",
	declined: "Declined",
	withdrawn: "Withdrawn",
};

export interface CareerListingMeta {
	slug: string;
	path: string;
	company: string;
	role: string;
	status: ListingStatus;
	url: string;
	deadline: string;
	posted_at: string;
	created_at: string;
	/**
	 * Async LLM enrichment status from the fast-save path.
	 *   ""          → no enrichment ever ran (legacy listing OR manual create)
	 *   "pending"   → save succeeded; LLM enrichment job queued
	 *   "complete"  → enrichment finished; company/role/etc. updated from LLM
	 *   "failed"    → enrichment timed out or LLM unavailable
	 *   "skipped"   → enrichment deliberately skipped (no raw_jd_text, etc.)
	 *   "no_queue"  → work queue unwired at save time (minimal install)
	 *   "enqueue_failed" → queue was wired but enqueue raised
	 */
	enrichment_status?: string;
	/**
	 * Career-coach screening (2026-08-16). ``verdict`` is the EFFECTIVE
	 * verdict — the user's override when set, else the coach's; empty =
	 * never screened. ``screen_status``: "" | pending | screened | failed |
	 * skipped. ``screen_verdict_source``: agent | user_override.
	 */
	screen_status?: string;
	screen_score?: number | null;
	screen_verdict?: ScreenVerdict | "";
	screen_user_verdict?: ScreenVerdict | "";
	screen_verdict_source?: string;
	verdict?: ScreenVerdict | "";
	screened_at?: string;
	screen_summary?: string;
	/** The user's notes, newest first, joined one per line (row preview). */
	notes?: string;
	/** True entry count of the note timeline (Rule 20). */
	notes_count?: number;
	has_notes?: boolean;
}

export const SCREEN_VERDICTS = ["strong", "possible", "weak"] as const;
export type ScreenVerdict = (typeof SCREEN_VERDICTS)[number];
export const SCREEN_VERDICT_LABELS: Record<ScreenVerdict, string> = {
	strong: "Strong match",
	possible: "Possible",
	weak: "Weak match",
};

export interface CareerListingsResponse {
	items: CareerListingMeta[];
	total: number;
	has_more: boolean;
}

export interface CareerListingDetail {
	slug: string;
	path: string;
	metadata: Record<string, unknown>;
	content: string;
}

export interface CareerListingCreate {
	company: string;
	role: string;
	url?: string;
	raw_jd_text?: string;
	status?: ListingStatus;
	source?: string;
	location?: string;
	salary_range?: string;
	deadline?: string;
	tech_stack?: string | string[];
	recruiter?: string;
	posted_at?: string;
}

export async function fetchCareerListings(
	opts: {
		status?: ListingStatus;
		verdict?: ScreenVerdict;
		screened?: boolean;
		limit?: number;
		offset?: number;
	} = {},
): Promise<CareerListingsResponse> {
	const params = new URLSearchParams();
	if (opts.status) params.set("status", opts.status);
	if (opts.verdict) params.set("verdict", opts.verdict);
	if (opts.screened !== undefined) params.set("screened", String(opts.screened));
	if (opts.limit !== undefined) params.set("limit", String(opts.limit));
	if (opts.offset !== undefined) params.set("offset", String(opts.offset));
	const qs = params.toString();
	const res = await fetch(`${BASE}/api/career/listings${qs ? "?" + qs : ""}`);
	await throwIfNotOk(res, "Failed to fetch career listings");
	return res.json();
}

export async function fetchCareerListing(slug: string): Promise<CareerListingDetail> {
	const res = await fetch(`${BASE}/api/career/listings/${encodeURIComponent(slug)}`);
	await throwIfNotOk(res, "Failed to fetch career listing");
	return res.json();
}

export interface CareerListingCreateResponse {
	slug: string;
	path: string;
	status: ListingStatus;
	created_at: string;
}

export async function createCareerListing(
	payload: CareerListingCreate,
): Promise<CareerListingCreateResponse> {
	const res = await fetch(`${BASE}/api/career/listings`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(payload),
	});
	await throwIfNotOk(res, "Failed to create career listing");
	return res.json();
}

/**
 * ``POST /api/career/listings/scrape`` — import a posting from a link,
 * deterministically and 1:1 (2026-08-16). No LLM in the request: the page
 * text becomes the JD body verbatim, company comes from the hostname, role
 * from the document title; structured fields enrich asynchronously.
 */
export interface CareerListingScrapeResponse extends CareerListingCreateResponse {
	company: string;
	role: string;
	enrichment_status: string;
	enrichment_job_id?: number | string;
	scrape: { backend: string; chars: number; title: string; content_type: string };
	derived: { company_from_hostname: boolean; role_from_title: boolean; body_verbatim: boolean };
}

/** The 502 detail of a failed scrape — Rule 26: reason + cause + remedies. */
export interface CareerListingScrapeFailure {
	error: string;
	reason: string;
	scraper_error: string;
	url: string;
	message: string;
	cause?: string;
	remedies?: { kind: "auto" | "manual" | "code"; label: string; detail?: string; action?: string }[];
	remediation_rule?: string;
}

export class CareerScrapeError extends Error {
	status: number;
	failure: CareerListingScrapeFailure | null;
	constructor(message: string, status: number, failure: CareerListingScrapeFailure | null) {
		super(message);
		this.name = "CareerScrapeError";
		this.status = status;
		this.failure = failure;
	}
}

export async function scrapeCareerListing(payload: {
	url: string;
	status?: ListingStatus;
	enrich?: boolean;
}): Promise<CareerListingScrapeResponse> {
	const res = await fetch(`${BASE}/api/career/listings/scrape`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(payload),
	});
	if (!res.ok) {
		let failure: CareerListingScrapeFailure | null = null;
		let message = `Failed to import listing: ${friendlyStatus(res.status)}`;
		try {
			const body = await res.json();
			const detail = body?.detail;
			if (detail && typeof detail === "object" && typeof detail.reason === "string") {
				failure = detail as CareerListingScrapeFailure;
				message = failure.cause || failure.message || message;
			} else if (typeof detail === "string") {
				message = detail;
			} else if (detail && typeof detail.message === "string") {
				message = detail.message;
			}
		} catch {
			/* keep the friendly status */
		}
		throw new CareerScrapeError(message, res.status, failure);
	}
	return res.json();
}

/** The coach's verdict fields as stamped on a listing (``screen_*`` frontmatter). */
export interface CareerScreenResult {
	screen_status: string;
	screen_score: number;
	screen_verdict: ScreenVerdict;
	screen_summary: string;
	screen_strengths: string[];
	screen_gaps: string[];
	screen_seniority: "under" | "match" | "over" | "";
	screen_model: string;
	screened_at: string;
	screen_error: string;
	screen_verdict_source: string;
	effective_verdict: ScreenVerdict | "";
}

export interface CareerScreenResponse {
	slug: string;
	ok: boolean;
	/** Present when ok. */
	screen?: CareerScreenResult;
	items_considered?: number;
	corrections_used?: number;
	/** Present when !ok — a short reason code + detail; the listing carries screen_status=failed. */
	reason?: string;
	screen_status?: string;
}

/** ``POST /api/career/listings/{slug}/screen`` — screen ONE listing now (one LLM call). */
export async function screenCareerListing(slug: string): Promise<CareerScreenResponse> {
	const res = await fetch(`${BASE}/api/career/listings/${encodeURIComponent(slug)}/screen`, {
		method: "POST",
	});
	await throwIfNotOk(res, "Failed to screen listing");
	return res.json();
}

/** ``POST /api/career/listings/screen`` — queue screening for many (async, work queue). */
export async function screenCareerListings(payload: {
	slugs?: string[];
	limit?: number;
	statuses?: string[];
} = {}): Promise<{
	queued: number;
	slugs: string[];
	skipped: { slug: string; reason: string }[];
	total_unscreened: number;
	capped: boolean;
}> {
	const res = await fetch(`${BASE}/api/career/listings/screen`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(payload),
	});
	await throwIfNotOk(res, "Failed to queue screening");
	return res.json();
}

/** ``PATCH /api/career/listings/{slug}/screen-verdict`` — YOUR verdict (Rule 27 correction); "" clears. */
export async function overrideCareerScreenVerdict(
	slug: string,
	verdict: ScreenVerdict | "",
	note = "",
): Promise<{
	slug: string;
	effective_verdict: ScreenVerdict | "";
	agent_verdict: ScreenVerdict | "";
	user_verdict: ScreenVerdict | "";
	screen_verdict_source: string;
}> {
	const res = await fetch(
		`${BASE}/api/career/listings/${encodeURIComponent(slug)}/screen-verdict`,
		{
			method: "PATCH",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ verdict, note }),
		},
	);
	await throwIfNotOk(res, "Failed to record verdict");
	return res.json();
}

/** ``POST /api/career/listings/{slug}/refetch`` — re-read the JD from the source link, 1:1 (Rule 29 exit). */
export interface CareerListingRefetchResponse {
	slug: string;
	chars: number;
	previous_chars: number;
	scrape: { backend: string; chars: number; title: string; content_type: string; extraction: string };
	filled_fields: string[];
	enrichment_status: string;
}

export async function refetchCareerListing(
	slug: string,
	opts: { enrich?: boolean } = {},
): Promise<CareerListingRefetchResponse> {
	const res = await fetch(`${BASE}/api/career/listings/${encodeURIComponent(slug)}/refetch`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ enrich: opts.enrich ?? true }),
	});
	if (!res.ok) {
		let failure: CareerListingScrapeFailure | null = null;
		let message = `Failed to re-fetch listing: ${friendlyStatus(res.status)}`;
		try {
			const body = await res.json();
			const detail = body?.detail;
			if (detail && typeof detail === "object" && typeof detail.reason === "string") {
				failure = detail as CareerListingScrapeFailure;
				message = failure.cause || failure.message || message;
			} else if (detail && typeof detail.message === "string") {
				message = detail.message;
			} else if (typeof detail === "string") {
				message = detail;
			}
		} catch {
			/* keep the friendly status */
		}
		throw new CareerScrapeError(message, res.status, failure);
	}
	return res.json();
}

export async function updateCareerListing(
	slug: string,
	patch: Partial<Omit<CareerListingCreate, "company" | "role"> & {
		company: string; role: string;
	}>,
): Promise<{ slug: string; path: string; updated_fields: Record<string, unknown> }> {
	const res = await fetch(`${BASE}/api/career/listings/${encodeURIComponent(slug)}`, {
		method: "PATCH",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(patch),
	});
	await throwIfNotOk(res, "Failed to update career listing");
	return res.json();
}

/** One dated entry of a listing's note timeline; `at` doubles as the id. */
export interface CareerListingNote {
	at: string;
	text: string;
}

export async function addCareerListingNote(
	slug: string,
	text: string,
): Promise<{ slug: string; note: CareerListingNote; notes_count: number }> {
	const res = await fetch(
		`${BASE}/api/career/listings/${encodeURIComponent(slug)}/notes`,
		{
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ text }),
		},
	);
	await throwIfNotOk(res, "Failed to add the note");
	return res.json();
}

export async function deleteCareerListingNote(
	slug: string,
	noteAt: string,
): Promise<{ slug: string; deleted: string; notes_count: number }> {
	const res = await fetch(
		`${BASE}/api/career/listings/${encodeURIComponent(slug)}/notes/${encodeURIComponent(noteAt)}`,
		{ method: "DELETE" },
	);
	await throwIfNotOk(res, "Failed to delete the note");
	return res.json();
}

export async function updateCareerListingStatus(
	slug: string,
	new_status: ListingStatus,
	note: string = "",
): Promise<{
	slug: string; path: string;
	status: ListingStatus; previous_status: ListingStatus; changed: boolean;
}> {
	const res = await fetch(
		`${BASE}/api/career/listings/${encodeURIComponent(slug)}/status`,
		{
			method: "PATCH",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ new_status, note }),
		},
	);
	await throwIfNotOk(res, "Failed to update listing status");
	return res.json();
}

export async function deleteCareerListing(
	slug: string,
): Promise<{ deleted: boolean; slug: string; reason?: string }> {
	const res = await fetch(`${BASE}/api/career/listings/${encodeURIComponent(slug)}`, {
		method: "DELETE",
	});
	await throwIfNotOk(res, "Failed to delete career listing");
	return res.json();
}

// ────────────────────────────────────────────────────────────────────
// Career — Phase 3 applications + Slice B/C analyze + build
// ────────────────────────────────────────────────────────────────────

export type ApplicationStatus = "draft" | "preparing" | "submitted" | "withdrawn";

export const APPLICATION_STATUSES: readonly ApplicationStatus[] = [
	"draft",
	"preparing",
	"submitted",
	"withdrawn",
] as const;

export const APPLICATION_STATUS_LABELS: Record<ApplicationStatus, string> = {
	draft: "Draft",
	preparing: "Preparing",
	submitted: "Submitted",
	withdrawn: "Withdrawn",
};

export interface CareerApplicationMeta {
	slug: string;
	path: string;
	folder: string;
	listing_slug: string;
	status: ApplicationStatus;
	created_at: string;
	generated_assets: string[];
}

export interface CareerApplicationsResponse {
	items: CareerApplicationMeta[];
	total: number;
	has_more: boolean;
}

export interface CareerApplicationDetail {
	slug: string;
	path: string;
	folder: string;
	metadata: Record<string, unknown>;
	content: string;
}

export interface CareerApplicationCreate {
	listing_slug: string;
	status?: ApplicationStatus;
}

export interface CareerApplicationCreateResponse {
	slug: string;
	path: string;
	folder: string;
	listing_slug: string;
	status: ApplicationStatus;
	created_at: string;
	attempt: number;
}

export async function fetchCareerApplications(
	opts: {
		status?: ApplicationStatus;
		listing_slug?: string;
		limit?: number;
		offset?: number;
	} = {},
): Promise<CareerApplicationsResponse> {
	const params = new URLSearchParams();
	if (opts.status) params.set("status", opts.status);
	if (opts.listing_slug) params.set("listing_slug", opts.listing_slug);
	if (opts.limit !== undefined) params.set("limit", String(opts.limit));
	if (opts.offset !== undefined) params.set("offset", String(opts.offset));
	const qs = params.toString();
	const res = await fetch(
		`${BASE}/api/career/applications${qs ? "?" + qs : ""}`,
	);
	await throwIfNotOk(res, "Failed to fetch career applications");
	return res.json();
}

export async function fetchCareerApplication(
	slug: string,
): Promise<CareerApplicationDetail> {
	const res = await fetch(
		`${BASE}/api/career/applications/${encodeURIComponent(slug)}`,
	);
	await throwIfNotOk(res, "Failed to fetch career application");
	return res.json();
}

export async function createCareerApplication(
	payload: CareerApplicationCreate,
): Promise<CareerApplicationCreateResponse> {
	const res = await fetch(`${BASE}/api/career/applications`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(payload),
	});
	await throwIfNotOk(res, "Failed to create career application");
	return res.json();
}

export async function updateCareerApplicationStatus(
	slug: string,
	new_status: ApplicationStatus,
	note: string = "",
): Promise<{
	slug: string; path: string;
	status: ApplicationStatus; previous_status: ApplicationStatus; changed: boolean;
}> {
	const res = await fetch(
		`${BASE}/api/career/applications/${encodeURIComponent(slug)}/status`,
		{
			method: "PATCH",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ new_status, note }),
		},
	);
	await throwIfNotOk(res, "Failed to update application status");
	return res.json();
}

export async function deleteCareerApplication(
	slug: string,
): Promise<{ deleted: boolean; slug: string; reason?: string; removed_files?: string[] }> {
	const res = await fetch(
		`${BASE}/api/career/applications/${encodeURIComponent(slug)}`,
		{ method: "DELETE" },
	);
	await throwIfNotOk(res, "Failed to delete career application");
	return res.json();
}

/** Closed enum of non-fatal reason codes for the build/analyze routes. */
export type ApplicationBuildReason =
	| "analyzer_disabled"
	| "builder_disabled"
	| "agent_not_wired"
	| "application_missing_listing_slug"
	| "missing_gap_report"
	| "listing_jd_empty"
	| "listing_not_found"
	| "no_facts_on_file"
	| "no_usable_signal"
	| "no_usable_content"
	| "docx_render_failed"
	| "analyzer_crashed"
	| "builder_crashed";

export interface ApplicationAnalyzeGapsResponse {
	ok: boolean;
	slug: string;
	listing_slug?: string;
	path?: string;
	cached?: boolean;
	reason?: ApplicationBuildReason;
	message?: string;
	covered_count?: number;
	partial_count?: number;
	missing_count?: number;
	total_requirements?: number;
	coverage_ratio?: number;
	honest_summary?: string;
	status_promoted?: boolean;
	previous_status?: ApplicationStatus;
}

export async function analyzeApplicationGaps(
	slug: string,
	opts: { force_refresh?: boolean } = {},
): Promise<ApplicationAnalyzeGapsResponse> {
	const res = await fetch(
		`${BASE}/api/career/applications/${encodeURIComponent(slug)}/analyze-gaps`,
		{
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(opts),
		},
	);
	await throwIfNotOk(res, "Failed to analyze application gaps");
	return res.json();
}

export interface ApplicationBuildCvResponse {
	ok: boolean;
	slug: string;
	listing_slug?: string;
	path?: string;
	cached?: boolean;
	reason?: ApplicationBuildReason;
	message?: string;
	experience_count?: number;
	education_count?: number;
	skills_count?: number;
	projects_count?: number;
	achievements_count?: number;
	status_promoted?: boolean;
	previous_status?: ApplicationStatus;
	/** Inline preview of the generated CV (markdown) so the user can read
	 * it without downloading the .docx. */
	preview_markdown?: string;
}

export async function buildApplicationCv(
	slug: string,
	opts: { force_refresh?: boolean } = {},
): Promise<ApplicationBuildCvResponse> {
	const res = await fetch(
		`${BASE}/api/career/applications/${encodeURIComponent(slug)}/build-cv`,
		{
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(opts),
		},
	);
	await throwIfNotOk(res, "Failed to build CV");
	return res.json();
}

export interface ApplicationBuildCoverLetterResponse {
	ok: boolean;
	slug: string;
	listing_slug?: string;
	path?: string;
	cached?: boolean;
	reason?: ApplicationBuildReason;
	message?: string;
	company?: string;
	role?: string;
	status_promoted?: boolean;
	previous_status?: ApplicationStatus;
	/** Inline preview of the generated cover letter (text) so the user can
	 * read it without downloading the .docx. */
	preview_markdown?: string;
}

export async function buildApplicationCoverLetter(
	slug: string,
	opts: { force_refresh?: boolean } = {},
): Promise<ApplicationBuildCoverLetterResponse> {
	const res = await fetch(
		`${BASE}/api/career/applications/${encodeURIComponent(slug)}/build-cover-letter`,
		{
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(opts),
		},
	);
	await throwIfNotOk(res, "Failed to build cover letter");
	return res.json();
}

// ─── Phase 4 — Privacy forget-all ──────────────────────────────────────────

export type CareerForgetAllCategory =
	| "facts"
	| "listings"
	| "applications"
	| "library"
	| "inbox";

export interface CareerForgetAllResult {
	wiped: boolean;
	total_files: number;
	categories: Record<
		CareerForgetAllCategory,
		{ file_count: number; removed_paths: string[] }
	>;
	errors: string[];
}

/**
 * GDPR-aligned wipe of every career data file. Two-step UX:
 *   - ``confirm: false`` (default) → returns inventory without deleting.
 *   - ``confirm: true`` → wipes facts/listings/applications/library/inbox
 *     contents (parent dirs survive per Rule 18 layout invariant).
 *
 * Caller MUST surface a confirm dialog before calling with ``confirm: true``.
 * Destructive + irreversible.
 */
export async function forgetCareerAll(
	confirm: boolean,
): Promise<CareerForgetAllResult> {
	const res = await fetch(`${BASE}/api/career/forget-all`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ confirm }),
	});
	await throwIfNotOk(res, "Failed to query career forget-all");
	return res.json();
}

// ─── Web clipper extension ─────────────────────────────────────────────
// Stubs added 2026-05-23 to unblock the parallel session's in-flight
// ExtensionPage.tsx so tsc + pre-commit hook can pass. Backend route at
// server/src/agntspace/api/routes/extension.py — shape mirrors its return
// dict. Owner: parallel session (web-clipper feature); free to refine.

export interface WebClipperInfo {
	available: boolean;
	path: string;
	version: string;
	file_count: number;
}

export async function fetchWebClipperInfo(): Promise<WebClipperInfo> {
	const res = await fetch(`${BASE}/api/extension/web-clipper/info`);
	await throwIfNotOk(res, "Failed to load web clipper info");
	return res.json();
}

export function webClipperDownloadUrl(): string {
	return `${BASE}/api/extension/web-clipper/download`;
}

// ── Outlook add-in ──
// `https` is the load-bearing field, not decoration: Outlook REFUSES to load
// an add-in whose manifest points at a non-HTTPS URL, so a user reading this
// page over plain http://127.0.0.1:8000 must be told to switch to the HTTPS
// listener BEFORE they sideload — otherwise the failure happens inside Outlook
// as a generic "we couldn't load this add-in", with nothing on our side to see.
export interface OutlookAddinInfo {
	available: boolean;
	path: string;
	version: string;
	file_count: number;
	manifest_url: string;
	origin: string;
	https: boolean;
}

export async function fetchOutlookAddinInfo(): Promise<OutlookAddinInfo> {
	const res = await fetch(`${BASE}/api/extension/outlook-addin/info`);
	await throwIfNotOk(res, "Failed to load Outlook add-in info");
	return res.json();
}

export function outlookAddinManifestUrl(): string {
	return `${BASE}/api/extension/outlook-addin/manifest.xml`;
}

// ── Finance quarantine — parked receipts that need attention ──
// The passive, LLM-independent surface (2026-07-05): receipts the watcher
// couldn't extract land in finance/inbox/quarantine/. Restore re-queues one
// for the watcher; delete removes it permanently.
export interface QuarantinedReceipt {
	filename: string;
	size: number;
	quarantined_at: string;
	failure_kind: string;
	reason: string;
	/** The model's advice for THIS receipt, from the quarantine sidecar's
	 *  `What to do:` line. Written since the watcher shipped; the reader used
	 *  to drop it, so it never reached a user until Rule 26 wiring. */
	suggested_action?: string;
	/** Rule 26 — from the shared failure-remediation catalog. */
	cause?: string;
	remedies?: Array<{
		kind: "auto" | "manual" | "code";
		label: string;
		detail?: string;
		action?: string;
	}>;
}

export async function getFinanceQuarantine(): Promise<QuarantinedReceipt[]> {
	const res = await fetch(`${BASE}/api/finance/quarantine`);
	await throwIfNotOk(res, "Failed to load quarantined receipts");
	const body = await res.json();
	// Rule 20 shape { items, total, has_more }; tolerate a bare array too.
	return Array.isArray(body) ? body : (body.items ?? []);
}

export async function restoreQuarantinedReceipt(filename: string): Promise<void> {
	const res = await fetch(
		`${BASE}/api/finance/quarantine/${encodeURIComponent(filename)}/restore`,
		{ method: "POST" },
	);
	await throwIfNotOk(res, "Failed to restore receipt");
}

export async function deleteQuarantinedReceipt(filename: string): Promise<void> {
	const res = await fetch(
		`${BASE}/api/finance/quarantine/${encodeURIComponent(filename)}`,
		{ method: "DELETE" },
	);
	await throwIfNotOk(res, "Failed to delete receipt");
}

// ── Finance VAT backfill — re-read receipts on disk and non-destructively
// fill missing MOMS/VAT on existing transactions. Dry-run by default. ──
export interface VatBackfillRow {
	txn_id: string;
	status: string;
	merchant?: string;
	total?: string;
	currency?: string;
	fills?: Record<string, string>;
}
export interface VatBackfillResult {
	commit: boolean;
	total: number;
	counts: Record<string, number>;
	results: VatBackfillRow[];
}

export async function backfillVat(
	commit: boolean,
	opts?: { limit?: number; model?: string },
): Promise<VatBackfillResult> {
	const res = await fetch(`${BASE}/api/finance/backfill-vat`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ commit, limit: opts?.limit, model: opts?.model ?? "" }),
	});
	await throwIfNotOk(res, commit ? "Failed to apply VAT backfill" : "Failed to run VAT backfill");
	return res.json();
}

// ── teach by recording (bots B5 half 2, 2026-09-07) ───────────────────
export interface Recording {
	id: string;
	host: string;
	agent_key: string;
	name: string;
	task: string;
	url: string;
	status: string; // starting | recording | stopped | cancelled | failed
	steps: string[];
	step_count: number;
	truncated: boolean;
	error: string;
	draft_id: string;
}

export interface RoutineDraftSummary {
	id: string;
	agent_key: string;
	name: string;
	task?: string;
	steps?: string[];
	proposed_by?: string;
}

async function detailOrThrow<T>(res: Response, fallback: string): Promise<T> {
	if (!res.ok) {
		const text = await res.text().catch(() => "");
		let msg = "";
		try {
			const d = JSON.parse(text)?.detail;
			msg = typeof d === "string" ? d : String(d?.message ?? "");
		} catch { /* not json */ }
		throw new Error(msg || `${fallback} (${friendlyStatus(res.status)})`);
	}
	return res.json() as Promise<T>;
}

export async function startRecording(body: { host: string; agent_key: string; name: string; task?: string; start_url?: string }): Promise<Recording> {
	const res = await fetch(`${BASE}/api/browser/record`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
	return (await detailOrThrow<{ recording: Recording }>(res, "Could not start the recording")).recording;
}

export async function fetchRecording(id: string): Promise<Recording> {
	const res = await fetch(`${BASE}/api/browser/record/${encodeURIComponent(id)}`);
	return (await detailOrThrow<{ recording: Recording }>(res, "Could not read the recording")).recording;
}

export async function fetchRecordings(): Promise<Recording[]> {
	const res = await fetch(`${BASE}/api/browser/recordings`);
	return (await detailOrThrow<{ recordings: Recording[] }>(res, "Could not list recordings")).recordings ?? [];
}

export async function stopRecording(id: string): Promise<{ recording: Recording; draft: RoutineDraftSummary | null; reason?: string }> {
	const res = await fetch(`${BASE}/api/browser/record/${encodeURIComponent(id)}/stop`, { method: "POST" });
	return detailOrThrow(res, "Could not stop the recording");
}

export async function cancelRecording(id: string): Promise<Recording> {
	const res = await fetch(`${BASE}/api/browser/record/${encodeURIComponent(id)}`, { method: "DELETE" });
	return (await detailOrThrow<{ recording: Recording }>(res, "Could not cancel the recording")).recording;
}

// --- Time reporting ---

export interface TimeMoney {
	[currency: string]: string;
}

export interface TimeWarning {
	code: string;
	message: string;
}

export interface TimeEntry {
	id: number;
	work_date: string;
	project_slug: string;
	project_title: string;
	subproject_slug: string;
	subproject_title: string;
	task_slug: string;
	task_title: string;
	time_code: string;
	time_code_name: string;
	minutes: number;
	hours: string;
	multiplier: string;
	billable: boolean;
	bill_rate: string;
	cost_rate: string;
	currency: string;
	bill_amount: string;
	cost_amount: string;
	bill_rate_override: string | null;
	cost_rate_override: string | null;
	note: string;
	status: string;
	source: string;
	started_at: string | null;
	ended_at: string | null;
}

export interface TimeCode {
	slug: string;
	name: string;
	names: Record<string, string>;
	kind: string;
	multiplier: string;
	billable: boolean;
	requires_project: boolean;
	color: string;
	active: boolean;
	sort_order: number;
	seeded: boolean;
	budget_minutes: number | null;
	budget_hours: string;
	budget_period: string;
}

export interface TimeRate {
	id: number;
	scope: string;
	project_slug: string;
	task_slug: string;
	bill_rate: string;
	cost_rate: string;
	currency: string;
}

export interface TimeSettings {
	currency: string;
	week_starts_on: string;
	default_bill_rate: string;
	default_cost_rate: string;
	hours_per_day?: string;
	minutes_per_day?: number;
	working_weekdays?: string[];
	holiday_calendar?: string;
}

export interface TimeTimer {
	started_at: string;
	planned_end_at?: string | null;
	project_slug: string;
	project_title?: string;
	subproject_slug: string;
	subproject_title?: string;
	task_slug: string;
	task_title?: string;
	time_code: string;
	time_code_name?: string;
	note: string;
	elapsed_seconds: number;
}

export interface TimeGridCell {
	minutes: number;
	entry_ids: number[];
	bill_amount_by_currency: TimeMoney;
}

export interface TimeGridRow {
	project_slug: string;
	project_title: string;
	subproject_slug: string;
	subproject_title: string;
	task_slug: string;
	task_title: string;
	time_code: string;
	time_code_name: string;
	billable: boolean;
	cells: Record<string, TimeGridCell>;
	minutes: number;
}

export interface TimeWeek {
	week_start: string;
	week_end: string;
	days: string[];
	week_starts_on: string;
	locked: boolean;
	locked_dates: string[];
	entries: TimeEntry[];
	rows: TimeGridRow[];
	totals: {
		minutes: number;
		billable_minutes: number;
		by_day: Record<string, { minutes: number; billable_minutes: number }>;
		bill_amount_by_currency: TimeMoney;
		cost_amount_by_currency: TimeMoney;
	};
	rollup: {
		projects: Array<{
			slug: string;
			title: string;
			minutes: number;
			direct_minutes: number;
			children: Array<{ slug: string; title: string; minutes: number; bill_amount_by_currency: TimeMoney; cost_amount_by_currency: TimeMoney }>;
			bill_amount_by_currency: TimeMoney;
			cost_amount_by_currency: TimeMoney;
		}>;
		codes: Array<{ slug: string; name: string; minutes: number; bill_amount_by_currency: TimeMoney; cost_amount_by_currency: TimeMoney }>;
	};
	timer: TimeTimer | null;
	coverage?: {
		scheduled_days: number;
		covered_days: number;
		short_days: number;
		short: { items: TimeYearDay[]; total: number; has_more: boolean };
	};
}

export interface TimeCatalog {
	codes: TimeCode[];
	settings: TimeSettings;
	rates: TimeRate[];
	timer: TimeTimer | null;
	policy: {
		kinds: string[];
		rate_cascade: string[];
		closed_project_statuses: string[];
		week_starts_on: string;
		default_currency: string;
		budget_periods: string[];
		budget_count_kinds: string[];
		code_budget_periods?: string[];
		minute_increment?: number;
		max_minutes_per_entry?: number;
		hours_per_day?: number;
		working_weekdays?: string[];
		year_cover_kinds?: string[];
		weekdays?: string[];
		calendars?: Array<{ id: string; name: string; names: Record<string, string> }>;
	};
}

export interface TimeHoliday {
	date: string;
	name: string;
	names?: Record<string, string>;
	source?: "calendar" | "custom";
	minutes?: number | null;
	hours?: string;
	on_working_day?: boolean;
}

export interface TimeHolidayList {
	items: TimeHoliday[];
	total: number;
	has_more: boolean;
	year: number;
}

export interface TimeYearDay {
	date: string;
	weekday: string;
	holiday: string;
	holiday_names?: Record<string, string>;
	minutes: number;
	hours: string;
	short_minutes: number;
	short_hours: string;
}

export interface TimeYearBalance {
	year: number;
	hours_per_day: string;
	minutes_per_day: number;
	uniform_day?: boolean;
	working_weekdays: string[];
	scheduled_days: number;
	holiday_days: number;
	days_off_holidays?: number;
	scheduled_minutes: number;
	scheduled_hours: string;
	planned_minutes: number;
	planned_hours: string;
	plan_gap_minutes: number;
	plan_gap_hours: string;
	plan_balanced: boolean;
	reported_minutes: number;
	reported_hours: string;
	off_schedule_minutes: number;
	off_schedule_hours: string;
	flex_minutes?: number;
	flex_hours?: string;
	covered_days: number;
	uncovered_days: number;
	days_covered: boolean;
	balanced: boolean;
	codes: Array<{
		slug: string;
		name: string;
		kind: string;
		minutes: number;
		hours: string;
		counts_toward_year: boolean;
	}>;
	uncovered: { items: TimeYearDay[]; total: number; has_more: boolean };
}

export interface TimeCodeBudget {
	slug: string;
	name: string;
	period: string;
	period_start: string;
	period_end: string;
	minutes: number;
	hours: string;
	spent_minutes: number;
	spent_hours: string;
	remaining_minutes: number;
	remaining_hours: string;
	over_minutes: number;
	over_hours: string;
}

export interface TimeCodeBudgetList {
	items: TimeCodeBudget[];
	total: number;
	has_more: boolean;
}

export interface TimeBudget {
	id: number;
	period: string;
	period_start: string;
	period_end: string;
	project_slug: string;
	project_title: string;
	minutes: number;
	hours: string;
	spent_minutes: number;
	spent_hours: string;
	remaining_minutes: number;
	remaining_hours: string;
	over_minutes: number;
	over_hours: string;
	note: string;
}

export interface TimeBudgetList {
	items: TimeBudget[];
	total: number;
	has_more: boolean;
	counts_kinds: string[];
}

export interface TimeBudgetInput {
	period: string;
	date?: string;
	start?: string;
	end?: string;
	project_slug?: string;
	hours?: string;
	note?: string;
}

export interface TimeEntryInput {
	work_date?: string;
	hours?: string;
	project_slug?: string;
	subproject_slug?: string;
	task_slug?: string;
	time_code: string;
	note?: string;
	billable?: boolean | null;
	bill_rate?: string | null;
	cost_rate?: string | null;
}

export interface TimeEntryResult {
	entry: TimeEntry;
	warnings: TimeWarning[];
}

async function readTime<T>(res: Response, action: string): Promise<T> {
	if (res.ok) return res.json() as Promise<T>;
	let code = "";
	let message = await responseError(res);
	try {
		const body = await res.clone().json();
		const detail = body?.detail;
		if (detail && typeof detail === "object") {
			if (typeof detail.error === "string") code = detail.error;
			if (typeof detail.message === "string") message = detail.message;
		}
	} catch { /* responseError already explained a non-JSON body */ }
	throw new Error(code ? `${action}: ${code} — ${message}` : `${action}: ${message}`);
}

export async function fetchTimeWeek(date?: string): Promise<TimeWeek> {
	const query = date ? `?date=${encodeURIComponent(date)}` : "";
	const res = await fetch(`${BASE}/api/time/week${query}`);
	return readTime(res, "Could not load the timesheet");
}

export async function fetchTimeCatalog(): Promise<TimeCatalog> {
	const res = await fetch(`${BASE}/api/time/catalog`);
	return readTime(res, "Could not load time setup");
}

export async function createTimeEntry(body: TimeEntryInput): Promise<TimeEntryResult> {
	const res = await fetch(`${BASE}/api/time/entries`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(body),
	});
	return readTime(res, "Could not log time");
}

export async function updateTimeEntry(entryId: number, body: TimeEntryInput): Promise<TimeEntryResult> {
	const res = await fetch(`${BASE}/api/time/entries/${entryId}`, {
		method: "PUT",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(body),
	});
	return readTime(res, "Could not update time");
}

export async function deleteTimeEntry(entryId: number): Promise<{ ok: boolean; id: number }> {
	const res = await fetch(`${BASE}/api/time/entries/${entryId}`, { method: "DELETE" });
	return readTime(res, "Could not delete time");
}

export async function startTimeTimer(body: {
	time_code: string;
	project_slug?: string;
	subproject_slug?: string;
	task_slug?: string;
	note?: string;
	planned_clock?: string;
	planned_duration?: string;
}): Promise<{ timer: TimeTimer }> {
	const res = await fetch(`${BASE}/api/time/timer/start`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(body),
	});
	return readTime(res, "Could not start the timer");
}

export async function planTimeTimer(body: {
	planned_clock?: string;
	planned_duration?: string;
	clear?: boolean;
}): Promise<{ timer: TimeTimer }> {
	const res = await fetch(`${BASE}/api/time/timer`, {
		method: "PUT",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(body),
	});
	return readTime(res, "Could not set the planned end");
}

export async function stopTimeTimer(): Promise<TimeEntryResult & { timer: null }> {
	const res = await fetch(`${BASE}/api/time/timer/stop`, { method: "POST" });
	return readTime(res, "Could not stop the timer");
}

export async function saveTimeSettings(body: Partial<TimeSettings>): Promise<TimeSettings> {
	const res = await fetch(`${BASE}/api/time/settings`, {
		method: "PUT",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(body),
	});
	return readTime(res, "Could not save time setup");
}

export async function createTimeCode(body: {
	name: string;
	slug?: string;
	kind: string;
	multiplier: string;
	billable: boolean;
	requires_project: boolean;
	color?: string;
	active?: boolean;
	budget_hours?: string;
	budget_period?: string;
}): Promise<TimeCode> {
	const res = await fetch(`${BASE}/api/time/codes`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(body),
	});
	return readTime(res, "Could not add the time code");
}

export async function updateTimeCode(slug: string, body: {
	name: string;
	kind: string;
	multiplier: string;
	billable: boolean;
	requires_project: boolean;
	color?: string;
	active?: boolean;
	name_is_custom?: boolean;
	budget_hours?: string;
	budget_period?: string;
}): Promise<TimeCode> {
	const res = await fetch(`${BASE}/api/time/codes/${encodeURIComponent(slug)}`, {
		method: "PUT",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(body),
	});
	return readTime(res, "Could not save the time code");
}

export async function deleteTimeCode(slug: string): Promise<{ ok: boolean; slug: string }> {
	const res = await fetch(`${BASE}/api/time/codes/${encodeURIComponent(slug)}`, { method: "DELETE" });
	return readTime(res, "Could not delete the time code");
}

export async function saveTimeRate(body: {
	scope: string;
	project_slug?: string;
	task_slug?: string;
	bill_rate?: string;
	cost_rate?: string;
	currency?: string;
}): Promise<TimeRate> {
	const res = await fetch(`${BASE}/api/time/rates`, {
		method: "PUT",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(body),
	});
	return readTime(res, "Could not save the hourly rate");
}

export async function deleteTimeRate(rateId: number): Promise<{ ok: boolean; id: number }> {
	const res = await fetch(`${BASE}/api/time/rates/${rateId}`, { method: "DELETE" });
	return readTime(res, "Could not remove the hourly rate");
}

export async function submitTimeWeek(date?: string): Promise<TimeWeek> {
	const res = await fetch(`${BASE}/api/time/weeks/submit`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ date: date ?? null }),
	});
	return readTime(res, "Could not submit the week");
}

export async function reopenTimeWeek(date?: string): Promise<TimeWeek> {
	const res = await fetch(`${BASE}/api/time/weeks/reopen`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ date: date ?? null }),
	});
	return readTime(res, "Could not reopen the week");
}

export async function recalculateTime(start?: string, end?: string): Promise<{ updated: number; skipped: number }> {
	const res = await fetch(`${BASE}/api/time/recalculate`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ start: start ?? null, end: end ?? null }),
	});
	return readTime(res, "Could not recalculate drafts");
}

export async function fetchTimeYearBalance(year: number): Promise<TimeYearBalance> {
	const res = await fetch(`${BASE}/api/time/year-balance?year=${encodeURIComponent(String(year))}`);
	return readTime(res, "Could not load the year");
}

export async function fetchTimeHolidays(year: number): Promise<TimeHolidayList> {
	const res = await fetch(`${BASE}/api/time/holidays?year=${encodeURIComponent(String(year))}`);
	return readTime(res, "Could not load holidays");
}

export interface TimeSchedule {
	project_slug: string;
	project_title: string;
	subproject_slug: string;
	subproject_title: string;
	task_slug: string;
	task_title: string;
	time_code: string;
	time_code_name: string;
	minutes: number | null;
	hours: string;
	days?: Array<{ weekday: string; minutes: number; hours: string }>;
}

export interface TimeFillResult {
	created: number;
	skipped: number;
	locked: number;
	unscheduled: number;
	empty: number;
	holiday_entries: number;
	work_entries: number;
	minutes: number;
	failed: { items: Array<{ date: string; error: string; message: string }>; total: number; has_more: boolean };
}

export async function fetchTimeSchedule(): Promise<TimeSchedule> {
	const res = await fetch(`${BASE}/api/time/schedule`);
	return readTime(res, "Could not load the normal day");
}

export async function saveTimeSchedule(body: {
	project_slug?: string;
	subproject_slug?: string;
	task_slug?: string;
	time_code?: string;
	hours?: string;
	days?: Array<{ weekday: string; hours?: string }>;
}): Promise<TimeSchedule> {
	const res = await fetch(`${BASE}/api/time/schedule`, {
		method: "PUT",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(body),
	});
	return readTime(res, "Could not save the normal day");
}

export async function fillTime(body: { start?: string; end?: string; year?: number }): Promise<TimeFillResult> {
	const res = await fetch(`${BASE}/api/time/fill`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(body),
	});
	return readTime(res, "Could not fill time");
}

export interface TimeCalendarDraft {
	key: string;
	date: string;
	title: string;
	start: string;
	end: string;
	minutes: number;
	hours: string;
	capped: boolean;
	project_slug: string;
	subproject_slug: string;
	task_slug: string;
	time_code: string;
	time_code_name: string;
	picked: string;
}

export interface TimeCalendarDraftList {
	items: TimeCalendarDraft[];
	total: number;
	has_more: boolean;
	calendar: boolean;
}

export async function fetchCalendarDrafts(start: string, end: string): Promise<TimeCalendarDraftList> {
	const params = new URLSearchParams();
	if (start) params.set("start", start);
	if (end) params.set("end", end);
	const query = params.toString();
	const res = await fetch(`${BASE}/api/time/calendar-drafts${query ? `?${query}` : ""}`);
	return readTime(res, "Could not load calendar meetings");
}

export async function acceptCalendarDraft(body: {
	key: string;
	date: string;
	project_slug?: string;
	subproject_slug?: string;
	task_slug?: string;
	time_code?: string;
	hours?: string;
	note?: string;
}): Promise<{ ok: boolean; entry: TimeEntry; warnings: string[] }> {
	const res = await fetch(`${BASE}/api/time/calendar-drafts/accept`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(body),
	});
	return readTime(res, "Could not log the meeting");
}

export async function dropCalendarDraft(body: { key: string; date: string }): Promise<{ ok: boolean; key: string; date: string }> {
	const res = await fetch(`${BASE}/api/time/calendar-drafts/drop`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(body),
	});
	return readTime(res, "Could not skip the meeting");
}

export async function copyTimeWeek(date: string): Promise<TimeFillResult> {
	const res = await fetch(`${BASE}/api/time/weeks/copy`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ date }),
	});
	return readTime(res, "Could not copy the previous week");
}

export async function saveTimeHoliday(body: { date: string; name: string; hours?: string }): Promise<TimeHoliday> {
	const res = await fetch(`${BASE}/api/time/holidays`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(body),
	});
	return readTime(res, "Could not save the holiday");
}

export async function deleteTimeHoliday(date: string): Promise<{ ok: boolean; date: string }> {
	const res = await fetch(`${BASE}/api/time/holidays/${encodeURIComponent(date)}`, { method: "DELETE" });
	return readTime(res, "Could not remove the holiday");
}

export async function fetchTimeCodeBudgets(date: string): Promise<TimeCodeBudgetList> {
	const query = date ? `?date=${encodeURIComponent(date)}` : "";
	const res = await fetch(`${BASE}/api/time/code-budgets${query}`);
	return readTime(res, "Could not load time-code limits");
}

export async function fetchTimeBudgets(start: string, end: string): Promise<TimeBudgetList> {
	const query = `?from=${encodeURIComponent(start)}&to=${encodeURIComponent(end)}`;
	const res = await fetch(`${BASE}/api/time/budgets${query}`);
	return readTime(res, "Could not load time budgets");
}

export async function saveTimeBudget(body: TimeBudgetInput): Promise<{ budget: TimeBudget; warnings: TimeWarning[] }> {
	const res = await fetch(`${BASE}/api/time/budgets`, {
		method: "PUT",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(body),
	});
	return readTime(res, "Could not save the time budget");
}

export async function deleteTimeBudget(budgetId: number): Promise<{ ok: boolean; id: number }> {
	const res = await fetch(`${BASE}/api/time/budgets/${budgetId}`, { method: "DELETE" });
	return readTime(res, "Could not remove the time budget");
}

export async function setProjectParent(slug: string, parent: string): Promise<{ slug: string; parent?: string }> {
	const res = await fetch(`${BASE}/api/projects/${encodeURIComponent(slug)}`, {
		method: "PUT",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ parent }),
	});
	return readTime(res, "Could not save the sub-project");
}

export async function createSubproject(title: string, parent: string): Promise<{ slug: string; title: string; parent: string }> {
	const res = await fetch(`${BASE}/api/projects`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ title, parent }),
	});
	return readTime(res, "Could not create the sub-project");
}
